<template>
  <!-- The wizard keeps its own `fl-editor` panel; the base modal supplies the mask, the Escape
       handling and the close-through-the-store plumbing every other modal uses. Chrome is off —
       the panel draws its own header, sidebar and footer. -->
  <modal
    class="fl-upgrade-modal"
    :id="modalId"
    :title="''"
    :show-divider="false"
    :show-footer="false"
    :is-closeable="false"
    size="flex"
  >
    <template v-slot:body>
      <section
        class="fl-editor w-[min(1050px,96vw)] h-[min(780px,94vh)] bg-white grid
               grid-cols-[230px_1fr] grid-rows-[76px_auto_1fr_68px]
               shadow-[0_30px_90px_rgba(0,0,0,0.24)]
               max-[900px]:grid-cols-[1fr] max-[900px]:grid-rows-[76px_auto_auto_1fr_68px]"
        role="dialog"
        aria-modal="true"
        aria-label="Complete your Foodleap profile"
      >
        <header class="col-span-full tw-flex items-center gap-5 px-6 py-5 border-b border-[var(--fl-line)]">
          <div class="tw-flex gap-5">
            <span class="text-[11px] uppercase tracking-[.1em]">Profile upgrade</span>
            <!-- The backend's completeness score, same number as the At-a-glance card. -->
            <strong
              class="text-[11px] text-[var(--fl-green)]"
              v-if="profileCompleteness !== null"
            >{{ profileCompleteness }}% complete</strong>
          </div>
          <i class="h-[5px] flex-1 bg-[#e7ece6]">
            <em class="block h-full bg-[var(--fl-green)]" :style="{ width: (profileCompleteness || 0) + '%' }"></em>
          </i>
          <!-- bg-transparent, not bg-none: Tailwind's bg-none only clears background-image. -->
          <button
            class="border-0 bg-transparent text-[27px] leading-none text-inherit cursor-pointer"
            aria-label="Close"
            @click="closeEditor"
          >×</button>
        </header>

        <!--
          Every message the wizard raises, in the panel that raised it.
          `foodleapProfileUpgrade:toast` puts it on the *page*, whose toast is a z-index 80 strip
          behind this z-index 150 dialog — so a failed save announced itself where nobody editing
          could see it, and in the page toast's success green at that. The page still gets the
          successes, which are what is worth reading once the wizard has closed over them.

          Its own grid row, under the header and above both panes rather than inside the scrolling
          body: in the body it was a sticky strip that still took its height out of the flow, so
          raising one shoved the whole step down and pinned a band over the top of it.

          Three things this row does deliberately:
            · It is always rendered and only its contents are conditional. Every pane here is
              auto-placed, so a row that came and went would move the aside and the body out of the
              `1fr` row and leave the footer above an empty one. Empty it has no height of its own,
              so the `auto` row collapses to nothing.
            · The tone sits on the row, not on the strip inside it, so the colour is the full width
              of the panel by construction rather than by however the strip happens to measure.
            · `grid-column` is set in the stylesheet below rather than as a utility class, because
              the span is load-bearing: without it the row lands in the 230px sidebar column.
        -->
        <div
          class="fl-notice-row"
          :class="notice ? (notice.tone === 'error' ? 'fl-notice--error' : 'fl-notice--ok') : ''"
        >
          <div
            class="fl-notice"
            v-if="notice"
            :role="notice.tone === 'error' ? 'alert' : 'status'"
          >
            <span class="fl-notice__text">{{ notice.text }}</span>
            <button
              class="fl-notice__action"
              type="button"
              v-if="notice.action"
              :disabled="isSavingStep"
              @click="runNoticeAction"
            >{{ notice.action.label }}</button>
            <button
              class="fl-notice__close"
              type="button"
              aria-label="Dismiss this message"
              @click="dismissNotice"
            >×</button>
          </div>
        </div>

        <aside class="bg-[var(--fl-pale)] py-6 overflow-auto max-[900px]:tw-flex max-[900px]:p-0">
          <button
            class="w-full bg-transparent text-left grid grid-cols-[28px_1fr_auto] gap-1 items-center
                   font-[inherit] text-inherit cursor-pointer border-0
                   px-6 py-4 max-[900px]:min-w-[150px]"
            :class="editorStep === index
              ? 'bg-white border-l-4 border-l-[var(--fl-green)] !pl-5'
              : ''"
            v-for="(step, index) of editorSteps"
            :key="step"
            @click="editorStep = index"
          >
            <b class="text-[9px] text-[var(--fl-muted)]">{{ pad(index + 1) }}</b>
            <span class="text-[11px]">{{ step }}</span>
            <em class="not-italic text-[var(--fl-green)]" v-if="stepComplete(index)">✓</em>
          </button>
        </aside>

        <div class="fl-editor__body px-12 py-10 overflow-auto max-[900px]:p-6" ref="body">
          <!-- 01 Organisation -->
          <template v-if="editorStep === 0">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">Tell the ecosystem who you are.</h2>
            <div class="fl-form-grid">
              <div class="fl-field fl-field--logo" v-if="canUploadLogo">
                <span>Logo</span>
                <div class="fl-logo-field">
                  <div class="fl-logo-upload">
                    <ImageInput
                      v-model="draft.logo"
                      :width="72"
                      :height="72"
                      :allow-remove="true"
                      @remove="draft.logo = ''"
                    />
                    <!-- Decoration only: ImageInput's file input covers the square and owns the click. -->
                    <span class="fl-logo-upload__plus" aria-hidden="true">+</span>
                  </div>
                  <small>PNG, JPEG or GIF · resized to 150px wide · max 4 MB.</small>
                </div>
              </div>
              <label class="fl-field">
                <span>Organisation name</span>
                <input type="text" v-model="draft.name">
              </label>
              <label class="fl-field">
                <span>One-line description</span>
                <input type="text" v-model="draft.tagline" maxlength="140" placeholder="What should someone remember?">
                <span class="fl-suggestion" v-if="suggestions.activities_description">
                  {{ suggestionNote('activities_description') }}
                  <button type="button" @click.prevent="toggleSuggestion('activities_description')">
                    {{ suggestionAction('activities_description') }}
                  </button>
                </span>
              </label>
              <label class="fl-field">
                <span>Website</span>
                <input type="text" v-model="draft.website" placeholder="https://">
                <span class="fl-suggestion" v-if="suggestions.url">
                  {{ suggestionNote('url') }}
                  <button type="button" @click.prevent="toggleSuggestion('url')">
                    {{ suggestionAction('url') }}
                  </button>
                </span>
              </label>
              <label class="fl-field">
                <span>Founded</span>
                <input type="text" inputmode="numeric" maxlength="4" v-model="draft.founded" placeholder="YYYY">
              </label>
              <!--
                `description` is an HTML property everywhere else it is touched — the classic editor
                writes it with rich-text-area, and the profile renders it through RichBodyContent —
                so it is edited here with the same editor. A plain textarea showed whoever had ever
                used the classic editor their own markup as literal text, and stripping those tags
                to make it readable silently flattened the published profile.

                A div rather than a label: the editor is a contenteditable, not a form control, so
                there is nothing for a label to point at.
              -->
              <div class="fl-field fl-field--rich">
                <span>What does the organisation do?</span>
                <rich-text-area
                  v-model="draft.description"
                  custom-placeholder="Describe activities, customers and distinctive capabilities."
                  no-height-limit
                />
                <span class="fl-suggestion" v-if="suggestions.description">
                  {{ suggestionNote('description') }}
                  <button type="button" @click.prevent="toggleSuggestion('description')">
                    {{ suggestionAction('description') }}
                  </button>
                </span>
              </div>
            </div>
          </template>

          <!-- 02 Contact & location -->
          <template v-else-if="editorStep === 1">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">How can the ecosystem reach you?</h2>
            <div class="fl-callout">
              Contact details are what turn a profile view into a conversation. Location drives
              the markets and proximity signals on your profile.
            </div>
            <div class="fl-form-grid">
              <label class="fl-field">
                <span>Contact email</span>
                <input type="email" v-model="draft.email" placeholder="hello@example.com">
                <span class="fl-suggestion" v-if="suggestions.email">
                  {{ suggestionNote('email') }}
                  <button type="button" @click.prevent="toggleSuggestion('email')">
                    {{ suggestionAction('email') }}
                  </button>
                </span>
              </label>
              <label class="fl-field">
                <span>Phone</span>
                <input type="text" v-model="draft.phone" placeholder="+32 …">
                <span class="fl-suggestion" v-if="suggestions.phone">
                  {{ suggestionNote('phone') }}
                  <button type="button" @click.prevent="toggleSuggestion('phone')">
                    {{ suggestionAction('phone') }}
                  </button>
                </span>
              </label>
              <label class="fl-field">
                <span>Street</span>
                <input type="text" v-model="draft.street">
              </label>
              <label class="fl-field fl-field--number">
                <span>Number</span>
                <input type="text" v-model="draft.number">
              </label>
              <label class="fl-field">
                <span>Postal code</span>
                <input type="text" v-model="draft.zip">
              </label>
              <label class="fl-field">
                <span>City</span>
                <input type="text" v-model="draft.city">
              </label>
              <div class="fl-field">
                <span>Country</span>
                <!-- The canonical country list, so `country_code` stays in step with the name.
                     Free text would let the two drift, and the code is what drives the markets
                     fact and the map. -->
                <foodleap-select
                  v-model="draft.countryCode"
                  :options="countryOptions"
                  placeholder="Choose a country…"
                />
              </div>
            </div>

            <p class="mt-6 mb-3 text-[10px] font-medium uppercase tracking-[.1em] text-[var(--fl-muted)]">
              Social channels
            </p>
            <p class="mt-0 mb-3 text-[12px] leading-[1.5] text-[var(--fl-muted)]">
              These appear as icons next to your name at the top of the profile. Paste the full
              address of each page.
            </p>
            <div class="fl-form-grid">
              <label class="fl-field" v-for="channel of socialChannels" :key="channel.key">
                <span>{{ channel.label }}</span>
                <input type="text" v-model="draft.socials[channel.key]" :placeholder="channel.placeholder">
                <span class="fl-suggestion" v-if="suggestions[channel.key]">
                  {{ suggestionNote(channel.key) }}
                  <button type="button" @click.prevent="toggleSuggestion(channel.key)">
                    {{ suggestionAction(channel.key) }}
                  </button>
                </span>
              </label>
            </div>

            <p class="mt-6 mb-3 text-[10px] font-medium uppercase tracking-[.1em] text-[var(--fl-muted)]">
              Other links
            </p>
            <p class="mt-0 mb-3 text-[12px] leading-[1.5] text-[var(--fl-muted)]">
              Anywhere else you are present — a shop, a project site, a profile on another platform.
              Your main website is above; adding it again here is ignored.
            </p>
            <div class="fl-evidence__row" v-for="(row, index) of draft.otherUrls" :key="'ou' + index">
              <input
                class="col-span-3 w-full border border-[var(--fl-line)] p-3 bg-white font-[inherit] text-[13px]
                       text-inherit focus:outline-none focus:border-[var(--fl-green)]"
                type="text"
                v-model="row.url"
                placeholder="https://"
              >
              <button
                class="border-0 bg-transparent text-[18px] leading-none text-[var(--fl-muted)] cursor-pointer
                       hover:text-[var(--fl-coral)]"
                aria-label="Remove link"
                @click="draft.otherUrls.splice(index, 1)"
              >×</button>
            </div>
            <button class="fl-add-row" @click="draft.otherUrls.push({ url: '' })">+ Add a link</button>
          </template>

          <!-- 03 Position & roles -->
          <template v-else-if="editorStep === 2">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">What do you bring to the ecosystem?</h2>
            <div class="fl-callout">
              Your type and stage place you in the ecosystem; roles and memberships describe what
              you actually do in it.
            </div>
            <div class="fl-form-grid">
              <div class="fl-field" v-if="categoryOptions.length">
                <span>{{ taxonomyLabel('category') }}</span>
                <foodleap-select v-model="draft.categoryId" :options="categoryOptions"/>
              </div>
              <div class="fl-field" v-if="stageOptions.length">
                <span>{{ taxonomyLabel('stage') }}</span>
                <foodleap-select v-model="draft.stageId" :options="stageOptions"/>
              </div>
              <!-- The hero stat reads current_employees[0] as a range. Both bounds are required
                   together, so this is sent as one record or not at all. -->
              <label class="fl-field">
                <span>Employees — from</span>
                <input type="text" inputmode="numeric" v-model="draft.employeesFrom" placeholder="10">
              </label>
              <label class="fl-field">
                <span>Employees — to</span>
                <input type="text" inputmode="numeric" v-model="draft.employeesTo" placeholder="50">
              </label>
            </div>

            <foodleap-taxonomy-picker
              v-model="draft.activityIds"
              :options="activityOptions"
              :label="taxonomyLabel('activities')"
              hint="The roles you fill in the ecosystem. Pick every one that applies."
              empty-label="No roles are configured for this ecosystem yet."
            />

            <foodleap-taxonomy-picker
              v-if="membershipOptions.length"
              v-model="draft.membershipIds"
              :options="membershipOptions"
              :label="taxonomyLabel('membership')"
              hint="Networks, clusters and programmes you belong to."
            />
          </template>

          <!-- 04 Transitions -->
          <template v-else-if="editorStep === 3">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">Your story across the transitions.</h2>
            <div class="fl-callout">
              Start by placing each transition — that is what makes you findable. Then tell your
              story for the ones that apply: it is what puts <em>your</em> words on your profile
              instead of our reading of them, and what makes your answer outrank our
              classification.
            </div>

            <p class="fl-editor__note" v-if="!editorTransitions.length">
              No transitions are configured for this ecosystem yet — an owner can add them under
              Configuration → Taxonomies.
            </p>

            <!-- The whole step, answerable in one click per transition. The narratives used to
                 sit in front of this, which put an afternoon's writing between the actor and the
                 cheapest, most useful answer on the page. -->
            <div class="fl-triage" v-if="editorTransitions.length">
              <div class="fl-triage__row" v-for="entry of editorTransitions" :key="'tr' + entry.key">
                <i :style="{ background: entry.color }"></i>
                <span class="fl-triage__name">
                  <b>{{ entry.label }}</b>
                  <small v-if="entry.score !== null && entry.score !== undefined">
                    {{ entry.score }}/100 suggested
                  </small>
                </span>
                <span class="fl-triage__choice" role="group" :aria-label="entry.label">
                  <button
                    v-for="option of relevanceOptions"
                    :key="entry.key + option.value"
                    :class="{ 'is-on': relevanceOf(entry.key) === option.value }"
                    :aria-pressed="relevanceOf(entry.key) === option.value ? 'true' : 'false'"
                    @click="setRelevance(entry.key, option.value)"
                  >{{ option.label }}</button>
                </span>
              </div>
            </div>

            <!-- Pulled up 12px: it belongs to the triage list above, whose own 22px bottom margin
                 would otherwise leave it floating. -->
            <p
              class="-mt-3 mb-6 text-[12px] leading-[1.5] text-[var(--fl-muted)]"
              v-if="hasSuggestedTransitions"
            >
              Pre-answered from what we already know about you — correct anything that is wrong.
              Nothing here is published as your own statement until you save it.
            </p>

            <!-- Story panels for the transitions that apply. Collapsed so the triage above stays
                 readable, but framed as work still outstanding rather than as an extra. -->
            <div
              class="tw-flex items-baseline justify-between gap-3 mt-6 mb-3"
              v-if="relevantTransitions.length"
            >
              <p class="m-0 text-[10px] font-medium uppercase tracking-[.1em] text-[var(--fl-muted)]">
                Your story, transition by transition
              </p>
              <span
                class="text-[11px] font-medium whitespace-nowrap"
                :class="substantiatedCount === relevantTransitions.length
                  ? 'text-[var(--fl-green)]'
                  : 'text-[var(--fl-ink)]'"
              >
                {{ substantiatedCount }} of {{ relevantTransitions.length }} substantiated
              </span>
            </div>
            <!-- No negative top margin here: this line follows the heading above, not the triage
                 list, so pulling it up ran it into the heading. -->
            <p
              class="mt-0 mb-4 text-[12px] leading-[1.5] text-[var(--fl-muted)]"
              v-if="relevantTransitions.length && substantiatedCount < relevantTransitions.length"
            >
              Open a transition below to write your story. Until you do, the profile shows our
              classification rather than your own account of it.
            </p>
            <!-- `group` so the chevron can react to the parent details being open. -->
            <details
              class="group border-t border-[var(--fl-line)]"
              v-for="entry of relevantTransitions"
              :key="'et' + entry.key"
            >
              <summary
                class="px-1 py-4 grid grid-cols-[12px_1fr_auto_12px] items-center gap-3
                       cursor-pointer list-none [&::-webkit-details-marker]:hidden
                       hover:bg-[var(--fl-pale)] transition-colors"
              >
                <i class="w-[8px] h-[8px] rounded-full" :style="{ background: entry.color }"></i>
                <span class="text-[12px]">{{ entry.label }}</span>
                <!-- Closed and empty, the right-hand slot is an invitation rather than a verdict:
                     "not substantiated yet" described a state without saying what to do about it. -->
                <b
                  class="text-[9px] uppercase tracking-[.08em] text-[var(--fl-green)]"
                  v-if="transitionSubstantiated(entry.key)"
                >Substantiated ✓</b>
                <b
                  class="text-[11px] font-medium text-[var(--fl-green)] underline decoration-dotted
                         underline-offset-2 group-open:invisible"
                  v-else
                >Tell your story</b>
                <!-- Rotates to point up once the panel is open. -->
                <svg
                  class="w-[12px] h-[12px] text-[var(--fl-muted)] transition-transform duration-200
                         group-open:-rotate-180"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </summary>
              <div class="fl-transition-form" v-if="draft.transitions[entry.key]">
                <!-- Our words, clearly marked as ours. Presenting a classification as something
                     the actor wrote would make the whole precedence rule meaningless. -->
                <p class="fl-suggestion" v-if="suggestionNote(entry.key)">
                  <b>Our draft, not your words yet.</b>
                  {{ suggestionNote(entry.key) }}
                  Edit it and save — from then on it is your statement, and it outranks ours.
                </p>
                <p class="fl-story-rule">
                  Ambition, activities and at least one source are what substantiate a claim —
                  that is the exact rule the API applies before your account outranks ours.
                </p>
                <label>
                  <span>Ambition</span>
                  <textarea
                    v-model="draft.transitions[entry.key].ambition"
                    placeholder="What are you trying to change, and by when?"
                  ></textarea>
                </label>
                <label>
                  <span>Activities &amp; practices</span>
                  <textarea
                    v-model="draft.transitions[entry.key].activities"
                    placeholder="What are you actually doing today?"
                  ></textarea>
                </label>
                <label>
                  <span>Intended outcomes</span>
                  <textarea
                    v-model="draft.transitions[entry.key].intended"
                    placeholder="What do you expect this to achieve?"
                  ></textarea>
                </label>
                <label>
                  <span>Observed outcomes</span>
                  <textarea
                    v-model="draft.transitions[entry.key].observed"
                    placeholder="What has already happened, with numbers where you have them?"
                  ></textarea>
                </label>
                <div class="fl-evidence">
                  <span>Evidence</span>
                  <p class="fl-evidence__hint">
                    A link, a report, a metric, or a person who can confirm it.
                  </p>
                  <!-- One row per source rather than a single free-text field: the API stores
                       evidence as records with a label, a URL and a kind. -->
                  <div
                    class="fl-evidence__row"
                    v-for="(item, itemIndex) of draft.transitions[entry.key].evidence"
                    :key="'ev' + entry.key + itemIndex"
                  >
                    <input type="text" v-model="item.label" placeholder="What is the proof?">
                    <input type="text" v-model="item.url" placeholder="https:// (optional)">
                    <foodleap-select v-model="item.kind" :options="evidenceKinds" placeholder="Kind…"/>
                    <button
                      class="fl-evidence__remove"
                      aria-label="Remove evidence"
                      @click="draft.transitions[entry.key].evidence.splice(itemIndex, 1)"
                    >×
                    </button>
                  </div>
                  <button class="fl-add-row" @click="addEvidenceRow(entry.key)">+ Add evidence</button>
                </div>
              </div>
            </details>
            <p class="fl-editor__note">
              Saved as your organisation's own statement. It takes precedence over our
              classification once you have provided the evidence behind it.
            </p>
          </template>

          <!-- 05 Offering & technology -->
          <template v-else-if="editorStep === 4">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">What you offer, and the technology behind it.</h2>

            <!--
              The profile's long-form "Offering" text (`additional_information`) — the actor's own
              answer to "what do you offer", which the taxonomy rows underneath classify. It is
              edited here rather than on step 1 because this is the section it is published in, and
              the step the profile's Offering header sends an owner to.

              Rich text, like `description`: the classic editor writes it with rich-text-area and
              the profile renders it through RichBodyContent, so a plain textarea here would show
              whoever used that editor their own markup as literal text.
            -->
            <div class="fl-field fl-field--rich" v-if="canEditOfferingDescription">
              <span>What do you offer?</span>
              <rich-text-area
                v-model="draft.offeringDescription"
                custom-placeholder="Products, services and the capabilities behind them."
                no-height-limit
              />
            </div>

            <div class="fl-callout">
              Two different statements about the same technology: what you supply to others, and
              what you run yourself.
            </div>

            <foodleap-taxonomy-picker
              v-model="draft.technologyIds"
              :options="technologyOptions"
              :label="taxonomyLabel('technology') + ' offered'"
              hint="What you develop, supply or operate for others."
            />

            <p class="fl-editor__sublabel">
              {{ taxonomyLabel('technology') }} adopted — what you use yourself, and how far you
              have taken it
            </p>
            <div class="fl-roadmap" v-if="draft.technologies.length">
              <div class="fl-roadmap__row" v-for="(row, index) of draft.technologies" :key="'rm' + index">
                <div class="fl-roadmap__cell">
                  <span>{{ taxonomyLabel('technology') }}</span>
                  <foodleap-select v-model="row.id" :options="technologyOptions"/>
                </div>
                <div class="fl-roadmap__cell">
                  <span>Status</span>
                  <foodleap-select v-model="row.status" :options="adoptionStatuses"/>
                </div>
                <label class="fl-roadmap__cell">
                  <span>Operational area</span>
                  <input type="text" v-model="row.area" placeholder="Production, R&amp;D…">
                </label>
                <label class="fl-roadmap__cell">
                  <span>Since</span>
                  <input type="text" inputmode="numeric" maxlength="4" v-model="row.startYear" placeholder="YYYY">
                </label>
                <button
                  class="fl-roadmap__remove"
                  aria-label="Remove technology"
                  @click="draft.technologies.splice(index, 1)"
                >×
                </button>
                <label class="fl-roadmap__cell fl-roadmap__cell--evidence">
                  <span>Evidence</span>
                  <input type="text" v-model="row.evidenceText" placeholder="Pilot report, certification…">
                </label>
                <label class="fl-roadmap__cell fl-roadmap__cell--link">
                  <span>Link</span>
                  <input type="text" v-model="row.evidenceUrl" placeholder="https://">
                </label>
              </div>
            </div>
            <button class="fl-add-row" @click="addTechnologyRow">+ Add technology</button>
            <p class="fl-editor__note">
              Saved as your roadmap. Rows you remove here come off the roadmap; the classification
              above is not affected.
            </p>
          </template>

          <!-- 06 Expertise -->
          <template v-else-if="editorStep === 5">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">Where does your expertise sit?</h2>
            <div class="fl-callout">
              This is what the profile shows beneath “Expertise, with a visible basis” — it is
              read straight from these lists.
            </div>

            <!-- Industries, not the `expertises` taxonomy: that one is user-level, declared by
                 people about themselves. The profile's expertise section reads industries. -->
            <foodleap-taxonomy-picker
              v-if="industryOptions.length"
              v-model="draft.industryIds"
              :options="industryOptions"
              :label="taxonomyLabel('industries')"
              hint="The sectors your team is genuinely good at."
            />

            <foodleap-taxonomy-picker
              v-if="businessAspectOptions.length"
              v-model="draft.businessAspectIds"
              :options="businessAspectOptions"
              :label="taxonomyLabel('business_aspects')"
            />

            <foodleap-taxonomy-picker
              v-if="readinessLevelOptions.length"
              v-model="draft.readinessLevelIds"
              :options="readinessLevelOptions"
              :label="taxonomyLabel('readiness_levels')"
            />

            <foodleap-taxonomy-picker
              v-if="processStepOptions.length"
              v-model="draft.processStepIds"
              :options="processStepOptions"
              :label="taxonomyLabel('process_steps')"
            />

            <!-- Picked from the artwork rather than a list: the goals are recognised by their
                 icon, and it is the same icon the profile shows. -->
            <div class="fl-sdg">
              <div class="fl-sdg__head">
                <p class="fl-editor__sublabel">Sustainable Development Goals</p>
                <span class="fl-sdg__count" v-if="draft.sustainabilityGoals.length">
                  {{ draft.sustainabilityGoals.length }} selected
                  <button type="button" class="fl-sdg__clear" @click="draft.sustainabilityGoals = []">
                    clear
                  </button>
                </span>
              </div>
              <p class="fl-sdg__hint">The UN goals your work contributes to.</p>
              <div class="fl-sdg__grid" role="group" aria-label="Sustainable Development Goals">
                <button
                  type="button"
                  v-for="goal of sustainabilityGoalOptions"
                  :key="'sdg' + goal.number"
                  :class="{ 'is-on': draft.sustainabilityGoals.includes(goal.value) }"
                  :aria-pressed="draft.sustainabilityGoals.includes(goal.value) ? 'true' : 'false'"
                  :title="goal.label"
                  :aria-label="goal.label"
                  @click="toggleSustainabilityGoal(goal)"
                >
                  <img :src="goal.image" :alt="goal.label" loading="lazy">
                </button>
              </div>
            </div>

            <p class="fl-editor__note" v-if="!hasExpertiseStepOptions">
              None of the expertise taxonomies are configured for this ecosystem, so the goals
              above are the only thing to fill in here.
            </p>
          </template>

          <!-- 07 Needs & offers -->
          <template v-else-if="editorStep === 6">
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">What would help you move faster?</h2>
            <div class="fl-callout">
              Specific needs and offers power more relevant matching.
            </div>

            <foodleap-taxonomy-picker
              v-model="draft.motivationIds"
              :options="motivationOptions"
              :label="'We are looking for'"
              hint="What you need from the ecosystem right now."
              empty-label="No needs are configured for this ecosystem yet."
            />

            <foodleap-taxonomy-picker
              v-model="draft.offerDomainIds"
              :options="domainOptions"
              :label="'We can offer'"
              :hint="'The ' + taxonomyLabel('domains').toLowerCase() + ' you sell a product or service in.'"
              empty-label="No domains are configured for this ecosystem yet."
            />

            <!--
              One remark per offered domain rather than one for all of them: what a company offers
              in dairy is rarely what it offers in packaging, and the pivot has always stored the
              remark per domain.
            -->
            <div class="fl-offer-remarks" v-if="offeredDomains.length">
              <p class="fl-editor__sublabel">Anything to add about what you offer?</p>
              <label class="fl-field" v-for="domain of offeredDomains" :key="'or' + domain.id">
                <span>
                  {{ domain.name }}
                  <em v-if="domain.parent" class="fl-offer-remarks__parent">in {{ domain.parent }}</em>
                </span>
                <textarea
                  rows="2"
                  v-model="draft.offerRemarks[domain.id]"
                  :placeholder="'Optional — what you offer in ' + domain.name + '.'"
                ></textarea>
              </label>
            </div>
          </template>

          <!-- 08 Products & services -->
          <template v-else>
            <span class="fl-overline">Step {{ editorStep + 1 }} of {{ editorSteps.length }}</span>
            <h2 class="text-[35px] tracking-[-0.035em] mt-4 mb-8">What do you actually sell?</h2>
            <div class="fl-callout">
              A product or service is a profile in its own right on Foodleap, linked to yours —
              which is what makes it findable on its own terms. Add one by name, then set it up
              here without leaving this wizard.
            </div>

            <!--
              Written on the spot rather than on Next, unlike every other step: adding one is two
              writes — createActor for the product's own profile, then the `has_product` link onto
              this actor — and a half-applied pair is not something to leave sitting in a draft.
            -->
            <div class="fl-field fl-products" v-if="canEditProducts">
              <span>{{ productRows.length ? 'On record' : 'Nothing on record yet' }}</span>
              <template v-for="(product, index) of productRows" :key="'wp' + product.id">
                <div class="fl-products__row" :class="{ 'is-open': isProductOpen(product.id) }">
                  <span>{{ product.name }}</span>
                  <button
                    class="fl-products__open"
                    type="button"
                    :aria-expanded="isProductOpen(product.id) ? 'true' : 'false'"
                    @click="toggleProductEditor(product)"
                  >{{ isProductOpen(product.id) ? 'Close' : 'Set up' }}</button>
                  <button
                    type="button"
                    :aria-label="'Remove ' + product.name"
                    :disabled="savingProduct"
                    @click="removeProduct(index)"
                  >×</button>
                </div>

                <!--
                  The product's own profile, edited in place — a product added from here would
                  otherwise sit on the ecosystem as a bare name until someone went and found its
                  page. Its fields, not this organisation's: `additional_information`, activities
                  and the rest are LegalEntity properties and are not asked for.
                -->
                <div class="fl-product-form" v-if="isProductOpen(product.id)">
                  <p class="fl-product-form__note" v-if="productEditor.loading">Loading…</p>
                  <template v-else-if="productEditor.draft">
                    <div class="fl-form-grid">
                      <div class="fl-field fl-field--logo">
                        <span>Image</span>
                        <div class="fl-logo-field">
                          <div class="fl-logo-upload">
                            <ImageInput
                              v-model="productEditor.draft.logo"
                              :width="72"
                              :height="72"
                              :allow-remove="true"
                              @remove="productEditor.draft.logo = ''"
                            />
                            <!-- Decoration only: ImageInput's file input covers the square. -->
                            <span class="fl-logo-upload__plus" aria-hidden="true">+</span>
                          </div>
                          <small>PNG, JPEG or GIF · resized to 150px wide · max 4 MB.</small>
                        </div>
                      </div>
                      <label class="fl-field">
                        <span>Name</span>
                        <input type="text" v-model="productEditor.draft.name">
                      </label>
                      <label class="fl-field">
                        <span>One-line description</span>
                        <input
                          type="text"
                          maxlength="140"
                          v-model="productEditor.draft.tagline"
                          placeholder="What is it, in one line?"
                        >
                      </label>
                      <label class="fl-field">
                        <span>Website</span>
                        <input type="text" v-model="productEditor.draft.website" placeholder="https://">
                      </label>
                      <div class="fl-field fl-field--rich">
                        <span>What does it do?</span>
                        <rich-text-area
                          v-model="productEditor.draft.description"
                          custom-placeholder="What it is, who it is for, and what makes it different."
                          no-height-limit
                        />
                      </div>
                    </div>

                    <!--
                      Almost every product is reached through the organisation that makes it, so
                      the organisation's details are the answer by default and the fields only
                      appear for the products that have their own. Ticked on its own when the
                      product carries nothing yet, or carries exactly what the organisation does.
                    -->
                    <div class="fl-product-form__section">
                      <p class="fl-product-form__heading">Contact &amp; location</p>
                      <!--
                        The native control is kept for the semantics and the keyboard, and the box
                        beside it is the one that is seen: the app's global `input[type=checkbox]`
                        draws its tick with an oversized ::after over an appearance:none box, which
                        lands in this panel as a black square. Same 14px box the taxonomy picker
                        uses, so the wizard has one checkbox and not two.
                      -->
                      <label class="fl-product-form__check">
                        <input type="checkbox" v-model="productEditor.draft.useOrganisationContact">
                        <i aria-hidden="true"></i>
                        <span>Use {{ actor.name || 'the organisation' }}’s contact details</span>
                      </label>
                      <p class="fl-product-form__note" v-if="productEditor.draft.useOrganisationContact">
                        {{ organisationContactSummary }}
                      </p>
                      <div class="fl-form-grid" v-else>
                        <label class="fl-field">
                          <span>Email</span>
                          <input type="email" v-model="productEditor.draft.email" placeholder="hello@example.com">
                        </label>
                        <label class="fl-field">
                          <span>Phone</span>
                          <input type="text" v-model="productEditor.draft.phone" placeholder="+32 …">
                        </label>
                        <label class="fl-field">
                          <span>Street</span>
                          <input type="text" v-model="productEditor.draft.street">
                        </label>
                        <label class="fl-field fl-field--number">
                          <span>Number</span>
                          <input type="text" v-model="productEditor.draft.number">
                        </label>
                        <label class="fl-field">
                          <span>Postal code</span>
                          <input type="text" v-model="productEditor.draft.zip">
                        </label>
                        <label class="fl-field">
                          <span>City</span>
                          <input type="text" v-model="productEditor.draft.city">
                        </label>
                        <div class="fl-field">
                          <span>Country</span>
                          <!-- The canonical list, so `country_code` stays in step with the name —
                               the code is what drives the map and the markets fact. -->
                          <foodleap-select
                            v-model="productEditor.draft.countryCode"
                            :options="countryOptions"
                            placeholder="Choose a country…"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="fl-product-form__section" v-if="hasProductTaxonomyOptions">
                      <p class="fl-product-form__heading">How it is classified</p>

                      <foodleap-taxonomy-picker
                        v-if="technologyOptions.length"
                        v-model="productEditor.draft.technologyIds"
                        :options="technologyOptions"
                        :label="taxonomyLabel('technology')"
                        hint="What this product is built on, or delivers."
                      />

                      <foodleap-taxonomy-picker
                        v-if="industryOptions.length"
                        v-model="productEditor.draft.industryIds"
                        :options="industryOptions"
                        :label="taxonomyLabel('industries')"
                        hint="The sectors it is meant for."
                      />

                      <foodleap-taxonomy-picker
                        v-for="group of productFeatureGroups"
                        :key="group.property"
                        v-model="productEditor.draft.features[group.property]"
                        :options="group.options"
                        :label="group.label"
                      />
                    </div>

                    <div class="fl-product-form__actions">
                      <button
                        class="fl-add-row"
                        type="button"
                        :disabled="productEditor.saving"
                        @click="saveProduct"
                      >{{ productEditor.saving ? 'Saving…' : 'Save this product ✓' }}</button>
                      <!-- Everything this form does not ask about lives there. A new tab, because
                           this one is holding an open wizard. -->
                      <a :href="`/actors/${product.id}`" target="_blank" rel="noopener">
                        Open its full profile ↗
                      </a>
                    </div>
                  </template>
                  <p class="fl-product-form__note" v-else>
                    This product could not be loaded. Its own profile page still has every field —
                    <a :href="`/actors/${product.id}`" target="_blank" rel="noopener">open it ↗</a>.
                  </p>
                </div>
              </template>
              <div class="fl-products__add">
                <input
                  type="text"
                  v-model="newProductName"
                  placeholder="Name of a product or service"
                  :disabled="savingProduct"
                  @keydown.enter.prevent="addProduct"
                >
                <button
                  class="fl-add-row"
                  type="button"
                  :disabled="savingProduct || !newProductName.trim()"
                  @click="addProduct"
                >{{ savingProduct ? 'Saving…' : '+ Add' }}</button>
              </div>
              <small class="text-[10px] leading-[1.6] text-[var(--fl-muted)]">
                Add one by name, then set it up here. Removing one unlinks it from this
                organisation; the product profile itself stays on Foodleap.
              </small>
            </div>

            <!--
              The read-only stand-in, for a payload that arrived without the `has_product` rows.
              The write detaches every link and re-attaches what it is sent, so a list we cannot
              see in full is not one we can safely edit — see canEditProducts.
            -->
            <div class="fl-field" v-else>
              <span>Products &amp; services on record</span>
              <ul class="fl-editor__list" v-if="productItems.length">
                <li v-for="product of productItems" :key="'wp' + product.id">{{ product.name }}</li>
              </ul>
              <small>
                This list could not be loaded in full, so it cannot be edited here — use
                “Edit connections” on the profile instead.
              </small>
            </div>
          </template>
        </div>

        <footer
          class="col-span-full border-t border-[var(--fl-line)] tw-flex items-center justify-between
                 gap-3 px-6 py-3 max-[900px]:col-start-1"
        >
          <button
            class="border-0 bg-transparent font-[inherit] text-[11px] text-inherit cursor-pointer"
            @click="editorStep > 0 ? editorStep-- : closeEditor()"
          >
            {{ editorStep > 0 ? '← Back' : 'Cancel' }}
          </button>
          <!--
            Deliberately quiet, and deliberately here: the wizard is where an owner meets every
            field on their profile, so it is where they notice what is wrong with one — and the
            floating Userback launcher is hidden site-wide, so without this there is no way to
            report it without abandoning the form.
          -->
          <span class="tw-flex items-center gap-3 text-[9px] text-[var(--fl-muted)] max-[560px]:hidden">
            Every step is saved as you go
            <button
              class="border-0 bg-transparent p-0 font-[inherit] text-[9px] text-inherit
                     cursor-pointer underline decoration-dotted underline-offset-2
                     hover:text-[var(--fl-ink)]"
              type="button"
              @click="sendStepFeedback"
            >
              Something wrong on this step?
            </button>
          </span>
          <!--
            One group, so the footer's `justify-between` still has three children to space: the
            Back/Cancel button, the saved-as-you-go note, and this. A fourth child would spread
            them all out instead.
          -->
          <span class="tw-flex items-center gap-2">
            <!--
              Same write as "Save & continue", without the advance. Only offered while there is a
              step to advance *to*: on the last step "Update my profile" already saves and closes,
              so a second button beside it would be the same action twice.

              Quiet fill rather than a border, so it reads as the secondary of the pair and still
              measures exactly as tall as the primary next to it — the two share their padding and
              both are borderless.
            -->
            <button
              class="border-0 font-[inherit] text-[11px] cursor-pointer bg-[var(--fl-pale)]
                     text-[var(--fl-ink)] px-5 py-3 rounded-[4px] disabled:opacity-50
                     disabled:cursor-default hover:bg-[var(--fl-mint)]"
              v-if="editorStep < lastStepIndex"
              :disabled="isSavingStep"
              @click="saveAndExit"
            >
              Save &amp; exit
            </button>
            <button
              class="border-0 font-[inherit] text-[11px] cursor-pointer bg-[var(--fl-green)] text-white
                     px-5 py-3 rounded-[4px] disabled:opacity-50 disabled:cursor-default"
              v-if="editorStep < lastStepIndex"
              :disabled="isSavingStep"
              @click="nextStep"
            >
              {{ isSavingStep ? 'Saving…' : 'Save & continue →' }}
            </button>
            <button
              class="border-0 font-[inherit] text-[11px] cursor-pointer bg-[var(--fl-green)] text-white
                     px-5 py-3 rounded-[4px] disabled:opacity-50 disabled:cursor-default"
              v-else
              :disabled="isSavingStep"
              @click="finishEditor"
            >
              {{ isSavingStep ? 'Saving…' : 'Update my profile ✓' }}
            </button>
          </span>
        </footer>
      </section>
    </template>
  </modal>
</template>

<script>
  import { defineComponent } from 'vue'

  import ImageInput from '@/components/Form/ImageInput.vue'
  import Modal from './Modal.vue'
  import FoodleapSelect from '@/pages/ActorDetail/FoodleapSelect.vue'
  import FoodleapTaxonomyPicker from '@/pages/ActorDetail/FoodleapTaxonomyPicker.vue'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'

  import CompanyMixin from '@/util/CompanyMixin.js'
  import ConfigMixin from '@/util/ConfigMixin.js'
  import TranslationsMixin from '@/util/TranslationsMixin.js'

  import MODAL_IDS from '@/constants/modal-ids.js'
  import { COUNTRIES } from '@/constants/countries'
  import { sustainabilityGoals, sustainabilityGoalsNL } from '@/constants/config.js'
  import { createActor, fetchActor, updateActor } from '@/api/actors.js'
  import { fetchActorSuggestions, resolveActorSuggestions } from '@/api/actor-suggestions.js'
  import { ACTION_TYPES as ACTORS_ACTION_TYPES } from '@/store/modules/actors.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import { updateActorTechnologyAdoptions } from '@/api/actor-technology-adoptions.js'
  import { updateActorTransitions } from '@/api/actor-transitions.js'
  import { trackHeapEvent } from '@/util/analytics.js'
  import { htmlText, isRelevantForActor } from '@/util/helpers.js'
  import { openFeedbackWidget } from '@/util/feedback-widget'

  // Matches the `logo` ceiling in ActorController::getAllowedImages().
  const LOGO_MAX_BASE64_LENGTH = 5592265

  // Fallback swatch for a transition with no `hex` configured on the taxonomy.
  const ACCENT_GREEN = '#78ad46'

  const EDITOR_STEPS = [
    'Organisation',
    'Contact & location',
    'Position & roles',
    'Transitions',
    'Offering & technology',
    'Expertise',
    'Needs & offers',
    'Products & services',
  ]

  /**
   * The profile upgrade wizard.
   *
   * Opened by committing SET_MODAL_CONTEXT with `{ actor, transitions, adoptions, products,
   * fallbackTransitions, step }` and then SHOW_MODAL with MODAL_IDS.PROFILE_UPGRADE — the same
   * contract every other modal in ModalsContainer follows.
   *
   * Results travel back over `$bus` rather than as emits: ModalsContainer renders the modal, so the
   * page that opened it is not this component's parent and cannot listen for emitted events.
   */
  export default defineComponent({
    name: 'foodleap-profile-upgrade-modal',
    components: {
      FoodleapSelect,
      FoodleapTaxonomyPicker,
      ImageInput,
      Modal,
      RichTextArea,
    },
    mixins: [CompanyMixin, ConfigMixin, TranslationsMixin],
    data () {
      return {
        modalId: MODAL_IDS.PROFILE_UPGRADE,
        editorStep: 0,
        editorSaving: false,
        savingStep: false,
        savingTransitions: false,
        draft: {},
        // Open suggestions, keyed by property, plus the value each one displaced. Keeping the
        // original is what makes "use the stored value" possible without a refetch.
        suggestions: {},
        suggestionOriginals: {},
        editorSteps: EDITOR_STEPS,
        // Local copies: a save refreshes these, and the page is told over $bus.
        actorTransitions: [],
        actorAdoptions: [],
        // The `has_product` rows as the API stores them: products are their own actor profiles
        // rather than a property of this one, so they live outside `draft` and are written on the
        // spot instead of on Next.
        productLinks: [],
        newProductName: '',
        savingProduct: false,
        // The product being set up in place, as `{ id, loading, saving, draft }`. One at a time:
        // two open forms on one screen is two sets of unsaved changes to lose.
        productEditor: null,
        // The one message on screen, as `{ text, tone, action }`. Successes clear themselves;
        // errors stay until they are dismissed or superseded, because they are the ones that have
        // to be read and often acted on.
        notice: null,
        noticeTimeout: null,
        // Mirrors Domain\Actor\Enums\TransitionRelevance — the API validates against these values.
        relevanceOptions: [
          { value: 'primary', label: 'Primary' },
          { value: 'secondary', label: 'Secondary' },
          { value: 'not_relevant', label: 'Not relevant' },
        ],
        // Mirrors Domain\Actor\Enums\TechnologyAdoptionStatus.
        adoptionStatuses: [
          { value: 'exploring', label: 'Exploring' },
          { value: 'piloting', label: 'Piloting' },
          { value: 'deployed', label: 'Deployed' },
          { value: 'scaled', label: 'Scaled' },
          { value: 'roadmap', label: 'Roadmap' },
          { value: 'discontinued', label: 'Discontinued' },
        ],
        // The channels the profile renders as icons beside the actor's name. `plural` marks the
        // two the backend stores as handle rows rather than a column, so they round-trip as an
        // array of one instead of a string.
        socialChannels: [
          { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/…' },
          { key: 'twitter', label: 'X / Twitter', placeholder: 'https://x.com/…', plural: true },
          { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/…', plural: true },
          { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/…' },
          { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@…' },
          { key: 'vimeo', label: 'Vimeo', placeholder: 'https://vimeo.com/…' },
          { key: 'crunchbase', label: 'Crunchbase', placeholder: 'https://crunchbase.com/organization/…' },
          { key: 'wikipedia', label: 'Wikipedia', placeholder: 'https://en.wikipedia.org/wiki/…' },
          { key: 'rss_feed', label: 'RSS feed', placeholder: 'https://…/feed.xml' },
        ],
        // Mirrors the `kind` values ActorTransitionController validates.
        evidenceKinds: [
          { value: 'link', label: 'Link' },
          { value: 'report', label: 'Report' },
          { value: 'metric', label: 'Metric' },
          { value: 'contact', label: 'Contact' },
        ],
      }
    },
    computed: {
      context () {
        return this.$store.state.ui.modalContext || {}
      },
      actor () {
        return this.context.actor || {}
      },
      /** Products are separate profiles derived from relationships, so the page hands them over. */
      productItems () {
        return this.context.products || []
      },
      /**
       * Whether the products block can be edited rather than only listed.
       *
       * A relationship write detaches every `has_product` link and re-attaches what it is sent, so
       * it is only safe once the payload carried the complete list. `productItems` is no substitute
       * for it: that one is the page's display list, capped at five and drawn from every
       * relationship type that happens to point at a Product.
       */
      canEditProducts () {
        return Array.isArray(this.actor.has_product)
      },
      /**
       * The product-feature taxonomies, as pickers.
       *
       * Only the ones the ecosystem has actually configured: these three are generic slots, so an
       * unconfigured one has no name worth showing and nothing to pick from.
       */
      productFeatureGroups () {
        return ['product_features_a', 'product_features_b', 'product_features_c']
          .map(property => ({
            property,
            label: this.taxonomyLabel(property),
            options: this.flattenTaxonomy(
              this.$store.state.taxonomies[this.productFeatureTaxonomy(property)],
              'children',
            ),
          }))
          .filter(group => group.options.length)
      },
      /**
       * Whether the product form has anything to classify with. Its section is dropped rather than
       * rendering a heading with nothing under it for an ecosystem that configured none of these.
       */
      hasProductTaxonomyOptions () {
        return this.technologyOptions.length > 0 ||
          this.industryOptions.length > 0 ||
          this.productFeatureGroups.length > 0
      },
      /** What "use the organisation's contact details" will actually write, in one line. */
      organisationContactSummary () {
        const address = this.actor.address || {}
        const parts = [
          this.actor.email,
          this.actor.phone,
          [address.street, address.number].filter(Boolean).join(' '),
          [address.zip, address.city].filter(Boolean).join(' '),
          address.country,
        ].map(part => (part || '').trim()).filter(Boolean)

        return parts.length
          ? parts.join(' · ')
          : 'Nothing on record for the organisation yet — fill in step 2 first, or untick this.'
      },
      /** The links as rows to render: `has_product` stores the id and the name of the other side. */
      productRows () {
        return this.productLinks.map(link => ({
          id: link.to,
          name: link.to_name || 'Unnamed product',
        }))
      },
      /**
       * What the transitions step asks about when the actor has no claims yet. The page owns this,
       * because working it out means reading the transition pulse and the innovation radar.
       */
      fallbackTransitions () {
        return this.context.fallbackTransitions || []
      },
      profileCompleteness () {
        const completeness = this.actor.completeness

        if (!Number.isFinite(completeness)) {
          return null
        }

        return Math.round(completeness <= 1 ? completeness * 100 : completeness)
      },
      resolvedLogo () {
        return this.logoUrl(this.actor)
      },
      // Mirrors ActorEditPanel: only legal entities carry a logo.
      canUploadLogo () {
        return this.actor.actor_type === 'LegalEntity'
      },
      localizedActivitiesDescription () {
        return this.localizedDisplayProperty(this.actor, 'activities_description')
      },
      rawDescription () {
        return this.localizedDisplayProperty(this.actor, 'description') || ''
      },
      rawOfferingDescription () {
        return this.localizedDisplayProperty(this.actor, 'additional_information') || ''
      },
      /**
       * `additional_information` is a LegalEntity property — isRelevantForActor is what the classic
       * editor gates the same field on, so the wizard asks for it on exactly the actor types that
       * can store it.
       */
      canEditOfferingDescription () {
        return isRelevantForActor('additional_information', this.actor)
      },
      foundingYear () {
        if (!this.actor.founding_date) {
          return null
        }

        return String(this.actor.founding_date).slice(0, 4)
      },
      /**
       * Taxonomy pickers. Read from state rather than the *Labels getters, which return names only —
       * writes need the ids.
       */
      categoryOptions () {
        const actorType = this.actor.actor_type || 'LegalEntity'

        // Categories are per actor type; offering a Person category to a company is a write the
        // backend silently drops, because storeCategoryRelationship looks the id up unfiltered.
        return (this.$store.state.taxonomies.categories || [])
          .filter(option => !option.actor_type || option.actor_type === actorType)
      },
      activityOptions () {
        return this.$store.state.taxonomies.activities || []
      },
      membershipOptions () {
        return this.$store.state.taxonomies.memberships || []
      },
      stageOptions () {
        return this.$store.state.taxonomies.stages || []
      },
      /** The platform's country list, sorted by name so the list is navigable. */
      countryOptions () {
        return Object.keys(COUNTRIES)
          .map(code => ({ value: code, label: COUNTRIES[code] }))
          .sort((a, b) => a.label.localeCompare(b.label))
      },
      businessAspectOptions () {
        return this.businessAspectTaxonomyEnabled
          ? (this.$store.state.taxonomies.businessAspects || [])
          : []
      },
      readinessLevelOptions () {
        return this.readinessTaxonomyEnabled
          ? (this.$store.state.taxonomies.readinessLevels || [])
          : []
      },
      processStepOptions () {
        return this.taxonomyProcessStepsEnabled
          ? (this.$store.state.taxonomies.processSteps || [])
          : []
      },
      businessAspectTaxonomyEnabled () {
        return this.$store.getters.businessAspectTaxonomyEnabled
      },
      readinessTaxonomyEnabled () {
        return this.$store.getters.readinessTaxonomyEnabled
      },
      taxonomyProcessStepsEnabled () {
        return this.$store.getters.taxonomyProcessStepsEnabled
      },
      /**
       * All 17 goals, as stored. The value written to the actor is always the English constant —
       * that is what the `sustainability_goal` facet indexes — while the label and artwork follow
       * the viewer's locale.
       */
      sustainabilityGoalOptions () {
        const isDutch = this.$store.getters.activeLocale === 'nl'
        const labels = isDutch ? sustainabilityGoalsNL : sustainabilityGoals
        const path = isDutch ? '/images/SDG_nl/' : '/images/SDG/'

        return sustainabilityGoals.map((value, index) => ({
          number: index + 1,
          value,
          label: labels[index],
          image: `${path}${index + 1}.jpg`,
        }))
      },
      hasExpertiseStepOptions () {
        return this.industryOptions.length > 0 ||
          this.businessAspectOptions.length > 0 ||
          this.readinessLevelOptions.length > 0 ||
          this.processStepOptions.length > 0
      },
      /**
       * The `industries` taxonomy, labelled with the ecosystem's own alias for it. Nothing links an
       * industry to a transition, so it sits with the other classification pickers.
       */
      industryOptions () {
        return this.flattenTaxonomy(this.$store.state.taxonomies.industries, 'industries')
      },
      motivationOptions () {
        return this.$store.state.taxonomies.motivations || []
      },
      domainOptions () {
        return this.flattenTaxonomy(this.$store.state.taxonomies.domains, 'domains')
      },
      /**
       * The picked domains, resolved to their taxonomy entry so each remark field can be labelled.
       *
       * Driven off `domainOptions` rather than the raw ids so the order stays the same as the
       * picker's, and an id the ecosystem no longer has drops out instead of rendering a nameless
       * box no one can interpret.
       */
      offeredDomains () {
        const selected = this.draft.offerDomainIds || []

        return this.domainOptions.filter(option => selected.includes(option.id))
      },
      technologyOptions () {
        return this.flattenTaxonomy(this.$store.state.taxonomies.technologies, 'children')
      },
      isSavingStep () {
        // `savingProduct` covers the create call, which happens before the link write flips
        // savingStep. The per-product form has its own button, but its write counts here too:
        // "Update my profile" closes the wizard, and closing over an in-flight write leaves the
        // form that is still holding the answers behind.
        return this.editorSaving || this.savingTransitions || this.savingStep ||
          this.savingProduct || !!(this.productEditor && this.productEditor.saving)
      },
      lastStepIndex () {
        return this.editorSteps.length - 1
      },
      /** The transitions the wizard asks about: recorded claims first, the page's fallback after. */
      editorTransitions () {
        if (this.actorTransitions.length) {
          return this.actorTransitions.map(row => ({
            key: String(row.transition.id),
            id: row.transition.id,
            label: row.transition.name,
            color: row.transition.hex || ACCENT_GREEN,
            contribution: row.activities || null,
            score: (row.score && row.score.score) ?? null,
            answer: row,
          }))
        }

        return this.fallbackTransitions
      },
      /**
       * The transitions worth writing a story about. "Not relevant" and unanswered are both
       * excluded — there is nothing to narrate either way.
       */
      relevantTransitions () {
        return this.editorTransitions.filter(entry => {
          const relevance = this.relevanceOf(entry.key)

          return relevance === 'primary' || relevance === 'secondary'
        })
      },
      /** How many of the applicable transitions carry a claim strong enough to outrank ours. */
      substantiatedCount () {
        return this.relevantTransitions
          .filter(entry => this.transitionSubstantiated(entry.key))
          .length
      },
      /**
       * True when anything on the transitions step was pre-answered by us.
       *
       * A computed, not a method: the template reads it without parentheses, and as a method that
       * evaluated the function object — always truthy — so the "pre-answered" hint showed even when
       * nothing had been suggested.
       */
      hasSuggestedTransitions () {
        return this.editorTransitions.some(entry => !!this.suggestionNote(entry.key))
      },
    },
    created () {
      this.actorTransitions = this.context.transitions || []
      this.actorAdoptions = this.context.adoptions || []
      this.productLinks = this.initialProductLinks()
      this.editorStep = Number(this.context.step) || 0
      this.draft = this.buildDraft()
      // Not awaited: the wizard opens on the stored values and the proposals drop in when they
      // arrive, rather than holding the modal on a spinner.
      this.fetchSuggestions()

      trackHeapEvent('foodleapActorDetail.openProfileUpgrade', { step: this.editorStep })
    },
    beforeUnmount () {
      if (this.noticeTimeout) {
        clearTimeout(this.noticeTimeout)
      }
    },
    watch: {
      /**
       * Each step is its own screen, and the body is the scroll container. Without this you scroll
       * to the bottom of one step, move to the next, and land halfway down it.
       *
       * Covers both ways of moving: the sidebar sets editorStep directly, the footer increments it.
       */
      editorStep () {
        // A message names a field on the step that raised it, and an offer to fix something is
        // spent the moment the person walks away from it.
        this.dismissNotice()

        this.$nextTick(() => {
          if (this.$refs.body) {
            this.$refs.body.scrollTop = 0
          }
        })
      },
    },
    methods: {
      pad (value) {
        return String(value).padStart(2, '0')
      },
      closeEditor () {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, this.modalId)
      },
      /**
       * A success. Shown in the panel and handed to the page, which is where it is still readable
       * after a save that closes the wizard.
       */
      showToast (message) {
        this.setNotice({ text: message, tone: 'ok' })
        this.$bus.emit('foodleapProfileUpgrade:toast', message)
      },
      /**
       * A failure, and optionally the one thing that would fix it as `{ label, run }`.
       *
       * Not handed to the page: its toast is styled as a success and sits behind this dialog, so
       * forwarding an error there is worse than not showing it at all.
       */
      showError (message, action = null) {
        this.setNotice({ text: message, tone: 'error', action })
      },
      setNotice (notice) {
        this.notice = notice

        if (this.noticeTimeout) {
          clearTimeout(this.noticeTimeout)
          this.noticeTimeout = null
        }

        if (notice.tone !== 'error') {
          this.noticeTimeout = setTimeout(() => {
            this.notice = null
            this.noticeTimeout = null
          }, 3200)
        }
      },
      dismissNotice () {
        this.notice = null

        if (this.noticeTimeout) {
          clearTimeout(this.noticeTimeout)
          this.noticeTimeout = null
        }
      },
      /** Run whatever the notice offered, and clear it — the offer is spent either way. */
      runNoticeAction () {
        const action = this.notice && this.notice.action

        this.dismissNotice()

        if (action && typeof action.run === 'function') {
          action.run()
        }
      },
      /**
       * Raise the Userback widget from inside the wizard.
       *
       * The context is set first, because it is what makes the report actionable: every step of
       * this wizard writes a different set of fields, so "the description field does not save"
       * needs to arrive already saying which step and which profile it was written on. Without it
       * a report from here is indistinguishable from one filed on the profile page.
       */
      sendStepFeedback () {
        const userback = this.$userback

        if (userback && typeof userback.setData === 'function') {
          // setData takes a flat object only — no nesting.
          userback.setData({
            source: 'foodleap-profile-wizard',
            wizard_step: `${this.editorStep + 1}. ${this.editorSteps[this.editorStep] || ''}`,
            actor_id: this.actor.id,
            actor_name: this.actor.name,
          })
        }

        openFeedbackWidget()
      },
      /** The ecosystem's own label for a taxonomy. */
      taxonomyLabel (taxonomy) {
        return this.getTaxonomyAliasWithDefault(taxonomy)
      },
      filled (value) {
        return !!String(value == null ? '' : value).trim()
      },
      /**
       * `filled` for an HTML property. An empty tiptap editor hands back `<p></p>`, which is a
       * non-empty string, so the plain check counted a description nobody had written as filled and
       * ticked step 1 complete. Judge the text, not the markup.
       */
      filledHtml (value) {
        return !!htmlText(value).trim()
      },
      /**
       * Flatten a two-level taxonomy into a pickable list: every parent, each followed by its own
       * children, carrying the parent name for context.
       *
       * `window.ALL_TAXONOMIES` nests children under a different key per taxonomy — `domains` under
       * `domains`, `industries` under `industries`, and the generic hierarchical ones (technologies,
       * product features) under `children`. Iterating the raw state flat only ever showed the top
       * level, which for domains is a heading rather than anything an actor is classified in.
       */
      flattenTaxonomy (values, childKey) {
        return (values || []).reduce((options, value) => {
          options.push({ id: value.id, name: value.name, parent: null })

          for (const child of value[childKey] || []) {
            options.push({ id: child.id, name: child.name, parent: value.name })
          }

          return options
        }, [])
      },
      /**
       * Ids out of an actor's taxonomy payload.
       *
       * The API emits two shapes. Simple taxonomies (activities, membership, motivations,
       * expertises, business_aspects, process_steps, readiness_levels) and single ones (category,
       * stage) arrive as `{ value, label }` from TransformIndexedActorProperties, while technology,
       * industries and domains keep `{ id, name }`. Reading only `.id` produced an empty list for
       * the first group, which then saved as an empty array and detached what the actor had.
       */
      taxonomyIds (values) {
        return (values || [])
          .map(item => this.taxonomyId(item))
          .filter(id => id !== '')
      },
      taxonomyId (value) {
        if (!value || typeof value !== 'object') {
          return value || ''
        }

        const id = value.value !== undefined && value.value !== null ? value.value : value.id

        return id === undefined || id === null ? '' : id
      },
      /**
       * Taxonomy payload for the properties whose store*Relationship reads `$item['id']` and that
       * the incoming transformer does not normalise — technology, industries and domains. Sending
       * bare ids for these is silently dropped server-side.
       */
      taxonomyPayload (ids) {
        return (ids || []).map(id => ({ id }))
      },
      /**
       * The actor's country as a code the select can bind to. Older records carry a country name
       * with no code, so fall back to a reverse lookup rather than showing an empty dropdown over a
       * country the actor already has.
       */
      existingCountryCode () {
        return this.countryCodeFrom(this.actor.address)
      },
      /** One stored address's country as a code the select can bind to. */
      countryCodeFrom (address) {
        const stored = address || {}

        if (stored.country_code && COUNTRIES[stored.country_code]) {
          return stored.country_code
        }

        if (!stored.country) {
          return ''
        }

        const match = Object.keys(COUNTRIES)
          .find(code => COUNTRIES[code].toLowerCase() === String(stored.country).toLowerCase())

        return match || ''
      },
      /** One bound of the actor's current employee range, as a string the input can bind to. */
      currentEmployees (bound) {
        const current = this.actor.current_employees && this.actor.current_employees[0]
        const value = current && current[bound]

        return value === null || value === undefined ? '' : String(value)
      },
      /**
       * The remarks already stored per domain, keyed by domain id.
       *
       * Not filtered to offered domains: a remark on a domain that is not currently offered is
       * still that domain's remark, and hiding it here would silently drop it the moment the user
       * ticks that domain and saves.
       */
      existingOfferRemarks () {
        return (this.actor.domains || []).reduce((remarks, domain) => {
          if (this.filled(domain.remark)) {
            remarks[domain.id] = domain.remark
          }

          return remarks
        }, {})
      },
      /**
       * Backing state for the transitions step, keyed exactly as editorTransitions is.
       *
       * Rebuilt after a save as well as on open: saving refreshes actorTransitions, which flips
       * editorTransitions from the fallback keys to transition ids. Without a rebuild the draft
       * would still be keyed the old way and every transition form would disappear.
       */
      /**
       * A proposed claim for one transition, when a suggestion carries one and no source has
       * answered it yet. A recorded claim always wins — a suggestion must never overwrite what
       * the actor, or our own classification, already said.
       */
      transitionSuggestionFor (entry) {
        const suggestion = this.suggestions.transitions

        if (!suggestion || !Array.isArray(suggestion.value)) {
          return null
        }

        return suggestion.value.find(row => {
          const named = String(row.transition ?? '').trim().toLowerCase()

          return named === String(entry.label || '').toLowerCase() ||
            named === String(entry.id || '')
        }) || null
      },
      transitionDraft () {
        return this.editorTransitions.reduce((byKey, entry) => {
          // An unanswered transition is where a proposal can land without displacing anything.
          const proposed = (entry.answer && entry.answer.relevance)
            ? null
            : this.transitionSuggestionFor(entry)
          const answer = proposed
            ? { ...proposed, intended_outcomes: proposed.intended_outcomes, source: 'suggestion' }
            : (entry.answer || {})

          return {
            ...byKey,
            [entry.key]: {
              id: entry.id || null,
              name: entry.label,
              relevance: answer.relevance || '',
              ambition: answer.ambition || '',
              activities: answer.activities || entry.contribution || '',
              intended: answer.intended_outcomes || '',
              observed: answer.observed_outcomes || '',
              // Where the text came from. Anything other than the actor itself is a draft we wrote
              // for them, and the form says so rather than presenting our words as theirs.
              suggestedFrom: proposed
                ? ((this.suggestions.transitions || {}).agent || 'suggestion')
                : (answer.source && answer.source !== 'actor_input'
                  ? answer.source
                  : (!answer.source && entry.contribution ? 'report' : '')),
              reviewedAt: answer.reviewed_at || '',
              // Copied rather than referenced, so editing the draft cannot mutate the fetched claim.
              // One blank row when there is nothing yet — unfilled rows are dropped on save.
              evidence: (answer.evidence || []).length
                ? answer.evidence.map(item => ({
                  label: item.label || '',
                  url: item.url || '',
                  kind: item.kind || '',
                }))
                : [{ label: '', url: '', kind: '' }],
            },
          }
        }, {})
      },
      buildDraft () {
        return {
          // Seeded with the resolved URL so the current logo is visible. Saving ignores anything
          // that is not a fresh `data:` upload, so this display value never round-trips.
          logo: this.resolvedLogo || '',
          name: this.actor.name || '',
          tagline: this.localizedActivitiesDescription || '',
          description: this.rawDescription,
          offeringDescription: this.rawOfferingDescription,
          website: this.actor.url || '',
          founded: this.foundingYear || '',
          email: this.actor.email || '',
          phone: this.actor.phone || '',
          street: (this.actor.address && this.actor.address.street) || '',
          number: (this.actor.address && this.actor.address.number) || '',
          zip: (this.actor.address && this.actor.address.zip) || '',
          city: (this.actor.address && this.actor.address.city) || '',
          countryCode: this.existingCountryCode(),
          // A row per link. storeOtherUrlsRelationship syncs on the url itself, so order and
          // duplicates do not matter; blank rows are dropped on save.
          otherUrls: (this.actor.other_urls || []).map(item => ({ url: item.url || '' })),
          socials: this.socialsDraft(),
          // Stored as the full "SDG 7: …" strings, so the draft holds them verbatim.
          sustainabilityGoals: (this.actor.sustainability_goal || []).slice(),
          categoryId: this.taxonomyId(this.actor.category),
          stageId: this.taxonomyId(this.actor.stage),
          employeesFrom: this.currentEmployees('lower_bound'),
          employeesTo: this.currentEmployees('upper_bound'),
          activityIds: this.taxonomyIds(this.actor.activities),
          membershipIds: this.taxonomyIds(this.actor.membership),
          industryIds: this.taxonomyIds(this.actor.industries),
          technologyIds: this.taxonomyIds(this.actor.technology),
          businessAspectIds: this.taxonomyIds(this.actor.business_aspects),
          readinessLevelIds: this.taxonomyIds(this.actor.readiness_levels),
          processStepIds: this.taxonomyIds(this.actor.process_steps),
          motivationIds: this.taxonomyIds(this.actor.motivations),
          offerDomainIds: (this.actor.domains || [])
            .filter(item => item.offers && item.offers.length)
            .map(item => item.id)
            .filter(Boolean),
          offerRemarks: this.existingOfferRemarks(),
          transitions: this.transitionDraft(),
          // Only what the actor has actually reported. Seeding rows from the technology
          // classification would invent adoption records — each carries a `reported_at` and is
          // stored as a dated observation.
          technologies: this.actorAdoptions.map(row => ({
            id: row.technology.id,
            status: row.adoption_status || '',
            area: row.operational_area || '',
            startYear: row.start_year ? String(row.start_year) : '',
            evidenceText: row.evidence_text || '',
            evidenceUrl: row.evidence_url || '',
          })),
        }
      },
      /**
       * One editable string per channel.
       *
       * Twitter and Facebook arrive as an array of handles because the backend keeps them in their
       * own tables; the profile shows the first, so that is the one the wizard edits.
       */
      /**
       * Pull the open suggestions and show them in place of what is stored.
       *
       * Failure is silent: suggestions are an enhancement, and a wizard that refuses to open
       * because an enrichment endpoint is down would be worse than one without proposals.
       */
      async fetchSuggestions () {
        try {
          const response = await fetchActorSuggestions(this.actor.id)

          this.suggestions = (response.suggestions || []).reduce((byProperty, suggestion) => {
            // `transitions` has no single input of its own — it is folded into the transitions
            // step by transitionDraft() — so it is kept even though draftFieldFor returns null.
            if (suggestion.property === 'transitions') {
              byProperty[suggestion.property] = suggestion
            } else if (this.draftFieldFor(suggestion.property) && this.suggestionText(suggestion)) {
              // A value the wizard cannot render as a single string is skipped rather than shown:
              // otherwise the note appears next to a field with nothing usable behind it.
              byProperty[suggestion.property] = suggestion
            }

            return byProperty
          }, {})

          this.applySuggestionsToDraft()

          // The draft was built before these arrived, so the transitions step still holds the
          // unanswered rows. Rebuilding is safe here: the person has not typed anything yet.
          if (this.suggestions.transitions) {
            this.draft.transitions = this.transitionDraft()
          }
        } catch (e) {
          this.suggestions = {}
        }
      },
      /**
       * Where a suggested property lands in the draft, or null when the wizard has no field for it.
       *
       * Suggestions arrive for properties this wizard does not ask about (jurisdiction, vat_number
       * and the relationship proposals); those are left to the curation screens.
       */
      draftFieldFor (property) {
        const direct = {
          description: 'description',
          url: 'website',
          email: 'email',
          phone: 'phone',
          activities_description: 'tagline',
        }

        if (direct[property]) {
          return { key: direct[property] }
        }

        return this.socialChannels.some(channel => channel.key === property)
          ? { key: 'socials', social: property }
          : null
      },
      readDraftField (field) {
        return field.social ? (this.draft.socials || {})[field.social] : this.draft[field.key]
      },
      writeDraftField (field, value) {
        if (field.social) {
          this.draft.socials[field.social] = value

          return
        }

        this.draft[field.key] = value
      },
      /**
       * A suggestion's value as a single editable string.
       *
       * Stored as JSON, so a property the backend keeps as a list (twitter, facebook) arrives as
       * an array even though the wizard has one input for it. Those two are stored as handle rows
       * — `[{ handle: '…' }]` (see ActorSuggestionRepository::addIdentifier) — so the row is
       * unwrapped rather than stringified, which is what turned Facebook into "[object Object]".
       * Anything else that is still an object has no single input to land in, so it reads as empty
       * and fetchSuggestions drops it.
       */
      suggestionText (suggestion) {
        const value = Array.isArray(suggestion.value) ? suggestion.value[0] : suggestion.value

        if (value === null || value === undefined) {
          return ''
        }

        if (typeof value === 'object') {
          const single = value.handle || value.url || value.name

          return single ? String(single) : ''
        }

        return String(value)
      },
      applySuggestionsToDraft () {
        for (const property of Object.keys(this.suggestions)) {
          const field = this.draftFieldFor(property)
          const suggested = this.suggestionText(this.suggestions[property])

          if (!field || !suggested) {
            continue
          }

          this.suggestionOriginals[property] = this.readDraftField(field)
          this.writeDraftField(field, suggested)
        }
      },
      /** Put the stored value back. The suggestion stays visible so it can be taken again. */
      restoreOriginal (property) {
        const field = this.draftFieldFor(property)

        if (field) {
          this.writeDraftField(field, this.suggestionOriginals[property] || '')
        }
      },
      useSuggestion (property) {
        const field = this.draftFieldFor(property)

        if (field) {
          this.writeDraftField(field, this.suggestionText(this.suggestions[property]))
        }
      },
      suggestionNote (property) {
        const agent = (this.suggestions[property] || {}).agent

        return this.isSuggestionApplied(property)
          ? `Filled in from a suggestion${agent ? ` by ${agent}` : ''}. Saving accepts it.`
          : `${agent || 'An agent'} suggested something different.`
      },
      suggestionAction (property) {
        return this.isSuggestionApplied(property) ? 'Use what was there' : 'Use the suggestion'
      },
      toggleSuggestion (property) {
        return this.isSuggestionApplied(property)
          ? this.restoreOriginal(property)
          : this.useSuggestion(property)
      },
      /** Whether the field currently holds what was suggested. */
      isSuggestionApplied (property) {
        const field = this.draftFieldFor(property)

        if (!field) {
          return false
        }

        return String(this.readDraftField(field) || '').trim() ===
          this.suggestionText(this.suggestions[property]).trim()
      },
      /**
       * Settle the suggestions belonging to the properties a step just saved.
       *
       * Called after the profile write, and only for properties that step owns — otherwise saving
       * step 1 would resolve a proposal for a field the person has not seen yet.
       *
       * Accepted values are sent as they were saved, so a person who took a suggestion and then
       * edited it is recorded as accepted_and_edited rather than a plain accept.
       */
      async resolveSuggestionsFor (properties) {
        const decisions = properties
          .filter(property => this.suggestions[property])
          .map(property => {
            const field = this.draftFieldFor(property)
            const saved = String(this.readDraftField(field) || '').trim()
            const suggested = this.suggestionText(this.suggestions[property]).trim()

            if (!saved || saved === String(this.suggestionOriginals[property] || '').trim()) {
              return { id: this.suggestions[property].id, status: 'denied' }
            }

            return {
              id: this.suggestions[property].id,
              status: saved === suggested ? 'accepted' : 'accepted_and_edited',
              accepted_value: saved,
            }
          })

        if (!decisions.length) {
          return
        }

        try {
          await resolveActorSuggestions(this.actor.id, decisions)

          for (const property of properties) {
            delete this.suggestions[property]
            delete this.suggestionOriginals[property]
          }
        } catch (e) {
          // The profile is already saved; a failure here only means the proposal stays open.
        }
      },
      /**
       * Settle the transitions proposal once claims have been written.
       *
       * One suggestion row covers every transition it proposed, so it is settled as a whole. The
       * saved claims go in as `accepted_value`, which is what makes it possible to see afterwards
       * how much of the proposal survived contact with the actor.
       */
      async resolveTransitionSuggestion (savedRows) {
        const suggestion = this.suggestions.transitions

        if (!suggestion) {
          return
        }

        try {
          await resolveActorSuggestions(this.actor.id, [{
            id: suggestion.id,
            status: 'accepted_and_edited',
            accepted_value: savedRows,
          }])

          delete this.suggestions.transitions
        } catch (e) {
          // The claims are saved; a failure here only leaves the proposal open.
        }
      },
      socialsDraft () {
        return this.socialChannels.reduce((draft, channel) => {
          const value = this.actor[channel.key]

          draft[channel.key] = (Array.isArray(value) ? value[0] : value) || ''

          return draft
        }, {})
      },
      /**
       * Only the channels the actor filled in, shaped the way each is stored. Sent on every save
       * of this step so clearing a field removes the icon.
       */
      toggleSustainabilityGoal (goal) {
        const selected = this.draft.sustainabilityGoals

        this.draft.sustainabilityGoals = selected.includes(goal.value)
          ? selected.filter(value => value !== goal.value)
          : selected.concat([goal.value])
      },
      socialsPayload () {
        return this.socialChannels.reduce((payload, channel) => {
          const value = ((this.draft.socials || {})[channel.key] || '').trim()

          payload[channel.key] = channel.plural ? (value ? [value] : []) : (value || null)

          return payload
        }, {})
      },
      relevanceOf (transitionKey) {
        const entry = this.draft.transitions && this.draft.transitions[transitionKey]

        return (entry && entry.relevance) || ''
      },
      setRelevance (transitionKey, relevance) {
        const entry = this.draft.transitions && this.draft.transitions[transitionKey]

        if (!entry) {
          return
        }

        entry.relevance = relevance
      },
      /**
       * Mirrors ActorTransition::isSubstantiated() exactly — evidence plus ambition plus activities.
       * Kept in step with the backend on purpose: a badge that promises precedence the API will not
       * grant is worse than no badge.
       */
      transitionSubstantiated (transitionKey) {
        const entry = this.draft.transitions && this.draft.transitions[transitionKey]

        if (!entry) {
          return false
        }

        if (entry.relevance === 'not_relevant') {
          return true
        }

        return this.filled(entry.ambition) &&
          this.filled(entry.activities) &&
          (entry.evidence || []).some(item => this.filled(item.label))
      },
      /**
       * Whether what is in this transition's fields is our draft rather than the actor's own words,
       * and where it came from.
       */
      suggestionNote (transitionKey) {
        const entry = this.draft.transitions && this.draft.transitions[transitionKey]

        if (!entry || !entry.suggestedFrom) {
          return null
        }

        const origin = entry.suggestedFrom === 'report'
          ? 'a published transition report'
          : 'our reading of your public information'

        return entry.reviewedAt
          ? `Drafted from ${origin}, ${entry.reviewedAt}.`
          : `Drafted from ${origin}.`
      },
      addEvidenceRow (transitionKey) {
        const entry = this.draft.transitions && this.draft.transitions[transitionKey]

        if (!entry) {
          return
        }

        entry.evidence.push({ label: '', url: '', kind: '' })
      },
      addTechnologyRow () {
        this.draft.technologies.push({
          id: '', status: '', area: '', startYear: '', evidenceText: '', evidenceUrl: '',
        })
      },
      /**
       * A step is complete when every field on it has been filled in.
       *
       * A step whose taxonomies the ecosystem has not configured counts as complete rather than
       * permanently unticked — there is nothing the actor could do about it.
       */
      stepComplete (index) {
        const draft = this.draft

        if (!draft || !draft.transitions) {
          return false
        }

        if (index === 0) {
          return this.filled(draft.name) && this.filled(draft.tagline) &&
            this.filledHtml(draft.description) && this.filled(draft.website) &&
            this.filled(draft.founded) &&
            (!this.canUploadLogo || this.filled(draft.logo))
        }

        if (index === 1) {
          return this.filled(draft.email) && this.filled(draft.city)
        }

        if (index === 2) {
          const categoryDone = !this.categoryOptions.length || this.filled(draft.categoryId)
          const activitiesDone = !this.activityOptions.length || (draft.activityIds || []).length > 0

          return categoryDone && activitiesDone
        }

        // Triage only: the narrative is what makes a claim outrank ours, but requiring it here would
        // leave the tick out of reach for an actor who answered the question we actually asked.
        if (index === 3) {
          const entries = this.editorTransitions

          return entries.length > 0 &&
            entries.every(entry => this.filled(this.relevanceOf(entry.key)))
        }

        if (index === 4) {
          return (draft.technologies || []).length > 0 &&
            draft.technologies.every(row =>
              this.filled(row.id) && this.filled(row.status) && this.filled(row.area))
        }

        if (index === 5) {
          return !this.hasExpertiseStepOptions || (draft.industryIds || []).length > 0
        }

        if (index === 6) {
          const needsDone = !this.motivationOptions.length || (draft.motivationIds || []).length > 0
          const offersDone = !this.domainOptions.length || (draft.offerDomainIds || []).length > 0

          return needsDone && offersDone
        }

        // Products: ticked once something is on record. A block that could not be loaded in full
        // is read-only, so it counts as complete for the same reason an unconfigured taxonomy
        // does — there is nothing the actor could do about it from here.
        return !this.canEditProducts || this.productRows.length > 0
      },
      /**
       * Persist whatever the current step owns, and report whether it landed.
       *
       * Held here rather than inside nextStep so that "Save & exit" commits exactly what
       * "Save & continue" commits — one list of savers, not two that drift apart as steps change.
       * `saveEditor(true)` keeps the wizard open: what happens after a successful write is the
       * caller's business, never the saver's. A saver that moved the step itself would make a step
       * that both advances and reports success skip the one after it.
       *
       * A step with nothing to persist counts as saved.
       */
      async saveCurrentStep () {
        const savers = {
          0: () => this.saveEditor(true),
          1: () => this.saveContact(),
          2: () => this.saveRoles(),
          3: () => this.saveTransitions(),
          4: () => this.saveRoadmap(),
          5: () => this.saveExpertise(),
          6: () => this.saveNeedsAndOffers(),
          // 7 — products, which are written as they are added rather than on Next.
        }

        const saver = savers[this.editorStep]

        if (!saver) {
          return true
        }

        return await saver() === true
      },
      // Every step persists something; each advances only once its write succeeds.
      async nextStep () {
        if (await this.saveCurrentStep()) {
          this.editorStep++
        }
      },
      /**
       * Save & exit: the step's own write, then out. Which step the wizard opens on next time is
       * the opener's call — `created` reads it from the modal context — so this deliberately does
       * not try to remember where it was left.
       *
       * Stays open when the write fails, for the same reason nextStep does not advance: the toast
       * the saver raised is the only account of what went wrong, and closing would take the form
       * that still holds the unsaved answers with it.
       */
      async saveAndExit () {
        if (await this.saveCurrentStep()) {
          this.closeEditor()
        }
      },
      /**
       * The final button: commit the last step, then the organisation step, then close.
       *
       * Through saveCurrentStep rather than a named saver, so "Update my profile" commits whatever
       * the last step happens to own — the products step writes as it goes and has no saver at
       * all, and hardcoding one here is how the step before it would silently stop being written
       * the next time a step is appended.
       *
       * saveEditor runs last because it is the one that closes the wizard — running it first would
       * dismiss the dialog while a later write was still in flight.
       */
      async finishEditor () {
        if (!await this.saveCurrentStep()) {
          return
        }

        await this.saveEditor(false)
      },
      /**
       * The server's own account of a failure, so "not saving" says why.
       *
       * Two shapes reach here. A handled failure answers `{ message }` — that is what the actor
       * controller returns for a save it could not complete. A rejected *validation* answers the
       * validator's bag flattened by `Handler::invalidJson`, with no `message` at all:
       * `{ name: ['An actor with similar properties already exists.'], id: ['abc'] }`. Reading only
       * `message` reported every one of those as the generic fallback, which hid the single line
       * that said what was actually wrong with the field.
       *
       * `id` and `statusCode` are skipped: neither is a message, and `id` is the duplicate's own
       * identifier — see duplicateActorId.
       */
      saveError (errors, fallback) {
        // A thrown Error is ours, not the server's: `message` on one of those is a line written for
        // whoever is reading the console, and putting it in front of an actor says nothing useful.
        if (!errors || typeof errors !== 'object' || errors instanceof Error) {
          return fallback
        }

        if (typeof errors.message === 'string' && errors.message.length) {
          return errors.message
        }

        const fieldMessage = Object.keys(errors)
          .filter(key => !['id', 'statusCode'].includes(key))
          .map(key => (Array.isArray(errors[key]) ? errors[key][0] : errors[key]))
          .find(value => typeof value === 'string' && value.length)

        return fieldMessage || fallback
      },
      /**
       * The transition and adoption endpoints answer with `written` and `skipped` rather than an
       * error status: a row naming something the ecosystem does not have is reported, not rejected.
       * Without this a save that stored nothing still showed a tick.
       */
      skippedRowsMessage (response) {
        const skipped = (response && response.skipped) || []

        if (!skipped.length) {
          return null
        }

        const reason = skipped[0].reason || 'it could not be matched'
        const subject = skipped[0].transition || skipped[0].technology || 'One entry'

        return skipped.length === 1
          ? `${subject} was not saved: ${reason}`
          : `${skipped.length} entries were not saved. ${subject}: ${reason}`
      },
      /**
       * One actor update, with a toast. Every step that writes actor properties goes through here so
       * the saving flag, the error message and the return contract are identical everywhere.
       */
      async saveActorProperties (data, successMessage, failureMessage) {
        this.savingStep = true

        try {
          await this.$store.dispatch(ACTORS_ACTION_TYPES.UPDATE_ACTOR, { id: this.actor.id, data })
          this.showToast(successMessage)

          return true
        } catch (errors) {
          this.showError(this.saveError(errors, failureMessage))
          console.error(errors)

          return false
        } finally {
          this.savingStep = false
        }
      },
      /** Step 1 — the organisation fields that map directly onto actor properties. */
      async saveEditor (keepOpen = false) {
        const data = {
          name: this.draft.name,
          activities_description: this.draft.tagline,
          description: this.draft.description,
          url: this.draft.website,
        }

        if (/^\d{4}$/.test(String(this.draft.founded || '').trim())) {
          data.founding_date = `${String(this.draft.founded).trim()}-01-01`
        }

        // The API turns a base64 data URI into a stored path (ActorController::saveImages), so only
        // send the logo when it actually changed: a freshly picked file, or a removal. Re-sending the
        // existing path would make the backend try to read it as image content.
        if (this.canUploadLogo) {
          const logo = this.draft.logo || ''

          if (logo.startsWith('data:')) {
            if (logo.length > LOGO_MAX_BASE64_LENGTH) {
              this.showError('That image is too large — please use one under 4 MB')

              return false
            }

            data.logo = logo
          } else if (!logo && this.actor.logo) {
            data.logo = ''
          }
        }

        this.editorSaving = true

        try {
          await this.$store.dispatch(ACTORS_ACTION_TYPES.UPDATE_ACTOR, { id: this.actor.id, data })
          await this.resolveSuggestionsFor(['description', 'url', 'activities_description'])
          this.showToast('Profile updated ✓')

          if (!keepOpen) {
            this.closeEditor()
          }

          return true
        } catch (errors) {
          this.showError(this.saveError(errors, 'Could not update the profile'))
          console.error(errors)

          return false
        } finally {
          this.editorSaving = false
        }
      },
      /** Step 2 — contact details and location. */
      async saveContact () {
        // Spread the stored address first: it also carries `locality`, which nothing here edits and
        // which the geo pipeline sets. The update overwrites the address wholesale, so anything not
        // copied forward is lost.
        const address = { ...(this.actor.address || {}) }
        const code = this.draft.countryCode || ''

        address.street = this.draft.street || null
        address.number = this.draft.number || null
        address.zip = this.draft.zip || null
        address.city = this.draft.city || null
        // Name and code are written together — the code drives the markets fact and the map, and a
        // pair that disagrees is worse than either alone.
        address.country = code ? COUNTRIES[code] : null
        address.country_code = code || null

        // Plain strings: the incoming transformer wraps each one as { url }. The store method
        // dedupes, drops anything equal to the actor's main website, and removes links no longer
        // present — so this is a full replace, like the roadmap.
        const otherUrls = (this.draft.otherUrls || [])
          .map(row => (row.url || '').trim())
          .filter(Boolean)

        const saved = await this.saveActorProperties(
          {
            email: this.draft.email || null,
            phone: this.draft.phone || null,
            address,
            other_urls: otherUrls,
            ...this.socialsPayload(),
          },
          'Contact details updated ✓',
          'Could not save the contact details',
        )

        if (saved) {
          await this.resolveSuggestionsFor(
            ['email', 'phone'].concat(this.socialChannels.map(channel => channel.key)),
          )
        }

        return saved
      },
      /** Step 3 — what kind of player, the roles it fills, and the groups it belongs to. */
      async saveRoles () {
        const data = {
          activities: this.draft.activityIds,
          membership: this.draft.membershipIds,
        }

        // Single-value taxonomies: omit rather than send an empty value, which would clear one the
        // actor already has.
        if (this.draft.categoryId) {
          data.category = this.draft.categoryId
        }

        if (this.draft.stageId) {
          data.stage = this.draft.stageId
        }

        // Both bounds are `required` in the nested rules, so a half-filled range is not a valid
        // record — send it only when both are numbers, and leave the existing one alone otherwise.
        const from = Number(String(this.draft.employeesFrom).trim())
        const to = Number(String(this.draft.employeesTo).trim())

        if (this.filled(this.draft.employeesFrom) && this.filled(this.draft.employeesTo) &&
            Number.isFinite(from) && Number.isFinite(to)) {
          data.employees = [{
            // Dated because employee counts are an observation, and the profile reads the newest.
            date: new Date().toISOString().slice(0, 10),
            lower_bound: Math.min(from, to),
            upper_bound: Math.max(from, to),
          }]
        }

        return this.saveActorProperties(data, 'Position updated ✓', 'Could not save this step')
      },
      // ── products & services ─────────────────────────────────────────────
      /**
       * Step 8's products block — the `has_product` links the actor already has.
       *
       * Read from the actor payload rather than from `context.products`: that one is the page's
       * display list, capped at five and stripped down to a name, so writing it back would drop
       * every link past the fifth. A payload without the rows at all leaves the block read-only
       * rather than editing a list we cannot see in full — see canEditProducts.
       */
      initialProductLinks () {
        return Array.isArray(this.actor.has_product)
          ? this.actor.has_product.map(row => ({ ...row }))
          : []
      },
      /**
       * One stored link as the pivot columns a relationship write keeps, and nothing else.
       *
       * The rows come back carrying the other side's name, logo and taxonomies alongside the
       * pivot; storeBelongsToManyRelationship drops those anyway. The metadata that *is* stored is
       * copied forward, because this step does not show `start`, `end`, `score`, `comment`,
       * `status` or the tags and sending the rows without them would clear them.
       */
      productLinkPayload (link) {
        return {
          to: link.to,
          start: link.start || null,
          end: link.end || null,
          score: link.score || null,
          comment: link.comment || null,
          status: link.status || null,
          tags: link.tags || [],
        }
      },
      /** Write the links and tell the page. */
      async writeProductLinks (links, successMessage, failureMessage) {
        const saved = await this.saveActorProperties(
          { has_product: links.map(this.productLinkPayload) },
          successMessage,
          failureMessage,
        )

        if (!saved) {
          return false
        }

        this.productLinks = links
        // The card on the profile is built from the actor's relationships, which this just changed.
        // `actorUpdate` is the event the profile pages already refetch on.
        this.$bus.emit('actorUpdate', this.actor.id)

        return true
      },
      /**
       * Add a product or service.
       *
       * Two writes, because a product is an actor in its own right: the first creates its profile,
       * the second links it to this one. Only the name is asked for — everything else about the
       * product belongs on the product's own page, and asking for it here would put a second form
       * in the middle of this step.
       */
      async addProduct () {
        const name = this.newProductName.trim()

        if (!name || this.savingProduct) {
          return
        }

        this.savingProduct = true

        try {
          const product = await createActor({
            id: null,
            actor_type: 'Product',
            name,
            actor_name: null,
            tags: [],
          })

          if (!product || !product.id) {
            throw new Error('createActor did not return a product')
          }

          const links = [
            ...this.productLinks,
            { to: product.id, to_name: product.name || name, start: null, end: null },
          ]

          if (await this.writeProductLinks(links, `${product.name || name} added ✓`, 'Could not link this product')) {
            this.newProductName = ''
            trackHeapEvent('foodleapActorDetail.addProduct', { actorId: this.actor.id })
            this.toggleProductEditor({ id: product.id, name: product.name || name })

            // An ecosystem with curation switched on stores a new profile as a draft, and a draft
            // is not rendered anywhere. The link is written either way, so this is the difference
            // between "adding it did not work" and "it is waiting to be approved" — which is the
            // whole reason this editor used to look unreliable.
            if (product.draft) {
              this.showToast(`${product.name || name} was added and is waiting for approval before it shows on your profile`)
            }
          }
        } catch (errors) {
          this.reportProductCreateFailure(errors, name)
        } finally {
          this.savingProduct = false
        }
      },
      /**
       * Why a product could not be created, and the one thing that would fix it.
       *
       * A duplicate is the common failure here and it is not a dead end: StoreActorRequest answers
       * one with the offending actor's own `elastic_id` — the same field AddActorModal reads to
       * route to it, and the same identifier a relationship write takes as `to` — so the profile
       * the person is after is already on Foodleap and can be linked rather than created a second
       * time. "An actor with similar properties already exists." on its own left them with nothing
       * to do but pick a different name, which is how duplicates get made.
       */
      reportProductCreateFailure (errors, name) {
        console.error(errors)

        const existingId = this.duplicateActorId(errors)

        if (!existingId) {
          this.showError(this.saveError(errors, 'Could not add this product'))

          return
        }

        if (this.productLinks.some(link => link.to === existingId)) {
          this.showError(`${name} is already listed under this organisation.`)

          return
        }

        this.showError(
          `“${name}” already has its own profile on Foodleap, so it cannot be added a second time.`,
          { label: 'Link the existing one', run: () => this.linkExistingProduct(existingId, name) },
        )
      },
      /**
       * The id of the actor a rejected create collided with, or null when it did not collide.
       *
       * `Handler::invalidJson` answers with the validator's bag flattened — `{ id: ['abc'], name:
       * ['An actor with similar properties already exists.'] }` — so the id arrives as a one-item
       * array of messages rather than a value. AddActorModal gets away with reading it raw because
       * it only ever concatenates it into a URL; a relationship write takes it as `to` and would
       * send an array.
       */
      duplicateActorId (errors) {
        const value = errors && errors.id

        const id = Array.isArray(value) ? value[0] : value

        return typeof id === 'string' && id.length ? id : null
      },
      /**
       * Link a product that already existed instead of creating a second profile for it.
       *
       * Its own name is fetched first rather than reusing what was typed: the match is on more than
       * the name — a url or a company number will do it — so the profile being linked can be called
       * something else entirely, and a row labelled with the search term would misname it until the
       * next refetch.
       */
      async linkExistingProduct (existingId, typedName) {
        if (this.savingProduct) {
          return
        }

        this.savingProduct = true

        try {
          let name = typedName

          try {
            const existing = await fetchActor(existingId)

            name = (existing && existing.name) || typedName
          } catch (errors) {
            // Not fatal: the link is what matters, and the row's label is corrected by the refetch
            // that follows the write.
            console.error(errors)
          }

          const links = [
            ...this.productLinks,
            { to: existingId, to_name: name, start: null, end: null },
          ]

          if (await this.writeProductLinks(links, `${name} linked ✓`, 'Could not link this product')) {
            this.newProductName = ''
            trackHeapEvent('foodleapActorDetail.linkExistingProduct', { actorId: this.actor.id })
            this.toggleProductEditor({ id: existingId, name })
          }
        } finally {
          this.savingProduct = false
        }
      },
      /**
       * Unlink, not delete: the product keeps its own profile and its own claim, it just stops
       * being listed under this organisation. Confirmed because re-adding by name would create a
       * second profile rather than restore the link.
       */
      async removeProduct (index) {
        const row = this.productLinks[index]

        if (!row || this.savingProduct) {
          return
        }

        const name = row.to_name || 'this product'

        if (!window.confirm(`Remove ${name} from your products & services? Its own profile stays on Foodleap.`)) {
          return
        }

        this.savingProduct = true

        try {
          await this.writeProductLinks(
            this.productLinks.filter((link, position) => position !== index),
            `${name} removed ✓`,
            'Could not remove this product',
          )
        } finally {
          this.savingProduct = false
        }

        // The form belonged to a product that is no longer linked.
        if (this.productEditor && this.productEditor.id === row.to) {
          this.productEditor = null
        }
      },
      /** Which state key holds a product-feature taxonomy. */
      productFeatureTaxonomy (property) {
        return {
          product_features_a: 'productFeaturesA',
          product_features_b: 'productFeaturesB',
          product_features_c: 'productFeaturesC',
        }[property]
      },
      isProductOpen (id) {
        return !!this.productEditor && this.productEditor.id === id
      },
      /**
       * Open (or close) the in-place editor for one product.
       *
       * The linked row only carries the other side's name and a handful of denormalised fields, so
       * the product's own profile is fetched: this form writes it back wholesale per taxonomy, and
       * a draft built from the row would clear everything the row does not happen to carry.
       */
      async toggleProductEditor (product) {
        if (this.isProductOpen(product.id)) {
          this.productEditor = null

          return
        }

        this.productEditor = { id: product.id, loading: true, saving: false, draft: null }

        try {
          const actor = await fetchActor(product.id)

          // A second row may have been opened while this was in flight.
          if (this.productEditor && this.productEditor.id === product.id) {
            this.productEditor.draft = this.productDraftFrom(actor)
          }
        } catch (errors) {
          console.error(errors)
        } finally {
          if (this.productEditor && this.productEditor.id === product.id) {
            this.productEditor.loading = false
          }
        }
      },
      /**
       * The product's stored values as an editable draft.
       *
       * `useOrganisationContact` starts on when the product has no contact details of its own, or
       * carries exactly the organisation's — which is the case for every product added from here,
       * and the reason the fields are hidden rather than pre-filled: a copy shown as the product's
       * own answer is indistinguishable from one somebody typed.
       */
      productDraftFrom (actor) {
        const email = actor.email || ''
        const phone = actor.phone || ''
        const address = actor.address || {}
        const hasAddress = ['street', 'number', 'zip', 'city', 'country', 'country_code']
          .some(key => this.filled(address[key]))
        const matchesOrganisation = (!email && !phone && !hasAddress) ||
          (email === (this.actor.email || '') && phone === (this.actor.phone || ''))

        return {
          logo: this.logoUrl(actor) || '',
          name: actor.name || '',
          tagline: this.localizedDisplayProperty(actor, 'activities_description') || '',
          description: this.localizedDisplayProperty(actor, 'description') || '',
          website: actor.url || '',
          email,
          phone,
          street: address.street || '',
          number: address.number || '',
          zip: address.zip || '',
          city: address.city || '',
          countryCode: this.countryCodeFrom(address),
          // Kept whole so the write can copy forward `locality` and anything else the geo pipeline
          // set on this product, exactly as step 2 does for the organisation.
          storedAddress: { ...address },
          useOrganisationContact: matchesOrganisation,
          technologyIds: this.taxonomyIds(actor.technology),
          industryIds: this.taxonomyIds(actor.industries),
          features: this.productFeatureGroups.reduce((features, group) => {
            features[group.property] = this.taxonomyIds(actor[group.property])

            return features
          }, {}),
        }
      },
      /**
       * Write the open product.
       *
       * Through the `updateActor` API rather than the store action every other saver uses: that one
       * commits the response to `actors.detail`, which is the organisation this wizard is editing,
       * and writing a product into it would swap the page's actor out from under it.
       */
      async saveProduct () {
        const editor = this.productEditor

        if (!editor || !editor.draft || editor.saving) {
          return
        }

        const draft = editor.draft
        const data = {
          name: draft.name,
          activities_description: draft.tagline,
          description: draft.description,
          url: draft.website,
          technology: this.taxonomyPayload(draft.technologyIds),
        }

        if (this.industryOptions.length) {
          data.industries = this.taxonomyPayload(draft.industryIds)
        }

        this.productFeatureGroups.forEach(group => {
          data[group.property] = this.taxonomyPayload(draft.features[group.property])
        })

        // Same rule as the organisation's logo: the API turns a base64 data URI into a stored path,
        // so re-sending the existing path would make it try to read that path as image content.
        const logo = draft.logo || ''

        if (logo.startsWith('data:')) {
          if (logo.length > LOGO_MAX_BASE64_LENGTH) {
            this.showError('That image is too large — please use one under 4 MB')

            return
          }

          data.logo = logo
        }

        if (draft.useOrganisationContact) {
          data.email = this.actor.email || null
          data.phone = this.actor.phone || null
          data.address = { ...(this.actor.address || {}) }
        } else {
          // The stored address first: it also carries `locality`, which nothing here edits and
          // which the geo pipeline sets. The update overwrites wholesale, so anything not copied
          // forward is lost.
          const address = { ...(draft.storedAddress || {}) }
          const code = draft.countryCode || ''

          address.street = draft.street || null
          address.number = draft.number || null
          address.zip = draft.zip || null
          address.city = draft.city || null
          // Name and code are written together; a pair that disagrees is worse than either alone.
          address.country = code ? COUNTRIES[code] : null
          address.country_code = code || null

          data.email = draft.email || null
          data.phone = draft.phone || null
          data.address = address
        }

        editor.saving = true

        try {
          const saved = await updateActor({ id: editor.id, data })
          const name = (saved && saved.name) || draft.name

          // The row is what the list renders, and the name may have just changed under it.
          this.productLinks = this.productLinks.map(link => (
            link.to === editor.id ? { ...link, to_name: name } : link
          ))

          this.showToast(`${name} updated ✓`)
          this.$bus.emit('actorUpdate', this.actor.id)
          trackHeapEvent('foodleapActorDetail.saveProduct', { actorId: editor.id })
          this.productEditor = null
        } catch (errors) {
          this.showError(this.saveError(errors, 'Could not save this product'))
          console.error(errors)
        } finally {
          if (this.productEditor && this.productEditor.id === editor.id) {
            this.productEditor.saving = false
          }
        }
      },
      /** Step 6 — expertise and the taxonomies alongside it. */
      async saveExpertise () {
        // Always sent: the picker shows all 17 regardless of ecosystem config, so an empty array
        // here genuinely means "none", not "never asked".
        const data = { sustainability_goal: this.draft.sustainabilityGoals }

        // Only send what the ecosystem actually has configured: an empty array is a valid "none", and
        // sending one for a taxonomy the wizard never showed would silently clear it.
        //
        // Objects rather than bare ids: storeIndustriesRelationship reads $item['id'] and the
        // incoming transformer does not normalise this taxonomy.
        if (this.industryOptions.length) {
          data.industries = this.taxonomyPayload(this.draft.industryIds)
        }

        if (this.businessAspectOptions.length) {
          data.business_aspects = this.draft.businessAspectIds
        }

        if (this.readinessLevelOptions.length) {
          data.readiness_levels = this.draft.readinessLevelIds
        }

        if (this.processStepOptions.length) {
          data.process_steps = this.draft.processStepIds
        }

        if (!Object.keys(data).length) {
          return true
        }

        return this.saveActorProperties(data, 'Expertise updated ✓', 'Could not save the expertise')
      },
      /** Step 5 — the offering prose, the technology offered, and the adopted roadmap. */
      async saveRoadmap () {
        const rows = (this.draft.technologies || [])
          .filter(row => !!row.id)
          .map(row => ({
            technology: row.id,
            adoption_status: row.status || null,
            operational_area: row.area || null,
            start_year: row.startYear || null,
            evidence_text: row.evidenceText || null,
            evidence_url: row.evidenceUrl || null,
          }))

        this.savingStep = true

        try {
          const data = { technology: this.taxonomyPayload(this.draft.technologyIds) }

          // Only for the actor types that showed the field: sending it for one that never did
          // would write an empty value over whatever is stored.
          if (this.canEditOfferingDescription) {
            data.additional_information = this.draft.offeringDescription
          }

          // Offered technology is a taxonomy on the actor; the roadmap is a separate record. Two
          // calls because they are two different kinds of statement about a technology.
          await this.$store.dispatch(ACTORS_ACTION_TYPES.UPDATE_ACTOR, { id: this.actor.id, data })

          const response = await updateActorTechnologyAdoptions(this.actor.id, rows)
          const skipped = this.skippedRowsMessage(response)

          this.actorAdoptions = (response && response.adoptions) || this.actorAdoptions
          this.$bus.emit('foodleapProfileUpgrade:adoptions', this.actorAdoptions)

          if (skipped) {
            this.showError(skipped)

            return false
          }

          this.showToast('Offering updated ✓')

          return true
        } catch (errors) {
          this.showError(this.saveError(errors, 'Could not save the offering'))
          console.error(errors)

          return false
        } finally {
          this.savingStep = false
        }
      },
      /**
       * Step 7 — needs and offers.
       *
       * Motivations are replaced wholesale, which is right: the picker holds the complete set.
       * Domains are not — a domain write replaces every domain the actor has, so the payload is
       * rebuilt from the actor's existing domains with only the `offers` flag toggled. Sending just
       * the selected ones would silently delete the rest of the actor's classification.
       */
      async saveNeedsAndOffers () {
        const selected = this.draft.offerDomainIds || []
        const existing = this.actor.domains || []
        const known = existing.map(domain => domain.id)
        const remarkFor = (id) => {
          const value = (this.draft.offerRemarks || {})[id]

          return this.filled(value) ? String(value).trim() : null
        }

        const domains = existing
          .map(domain => {
            const offered = selected.includes(domain.id)

            return {
              id: domain.id,
              offers: offered ? ['service'] : [],
              // Only the offered domains take their remark from this step's fields; the rest are
              // not on screen here, so they keep whatever they had rather than being cleared by a
              // form that never asked about them.
              remark: offered ? remarkFor(domain.id) : (domain.remark || null),
            }
          })
          .concat(
            selected
              .filter(id => !known.includes(id))
              .map(id => ({ id, offers: ['service'], remark: remarkFor(id) })),
          )

        return this.saveActorProperties(
          { motivations: this.draft.motivationIds, domains },
          'Needs and offers updated ✓',
          'Could not save needs and offers',
        )
      },
      /**
       * Step 4 — the transitions. Everything written here is the actor's own declaration: the API
       * stores it as `actor_input`, which is what lets it outrank our classification.
       */
      async saveTransitions () {
        const rows = Object.keys(this.draft.transitions || {})
          .map(key => this.draft.transitions[key])
          // A transition with no relevance chosen has not been answered; leave it alone rather than
          // recording a default the actor never picked.
          .filter(row => !!row.relevance)
          .map(row => ({
            transition: row.id || row.name,
            relevance: row.relevance,
            ambition: row.ambition || null,
            activities: row.activities || null,
            intended_outcomes: row.intended || null,
            observed_outcomes: row.observed || null,
            // A row with no label is an empty form row the user never filled in, not a deletion.
            evidence: (row.evidence || [])
              .filter(item => this.filled(item.label))
              .map(item => ({
                label: item.label,
                url: this.filled(item.url) ? item.url : null,
                kind: this.filled(item.kind) ? item.kind : null,
              })),
          }))

        // Nothing triaged yet. Skipping the step is allowed, so this is a success — but there is no
        // update to announce.
        if (!rows.length) {
          return true
        }

        this.savingTransitions = true

        try {
          const response = await updateActorTransitions(this.actor.id, rows)
          const skipped = this.skippedRowsMessage(response)

          this.actorTransitions = (response && response.transitions) || this.actorTransitions
          this.$bus.emit('foodleapProfileUpgrade:transitions', this.actorTransitions)

          // Settled before the draft is rebuilt: once the claims are recorded the proposal has
          // been acted on, whether the person kept it as-is or rewrote it.
          await this.resolveTransitionSuggestion(rows)

          // editorTransitions is now keyed by transition id rather than by the fallback keys, so the
          // draft has to follow or the forms lose their backing state.
          this.draft.transitions = this.transitionDraft()

          if (skipped) {
            this.showError(skipped)

            return false
          }

          this.showToast('Transitions updated ✓')

          return true
        } catch (errors) {
          this.showError(this.saveError(errors, 'Could not save the transitions'))
          console.error(errors)

          return false
        } finally {
          this.savingTransitions = false
        }
      },
    },
  })
</script>
<style lang="scss" scoped>
  // ModalsContainer renders outside `.foodleap-actor-detail`, where these custom properties are
  // declared, so the palette is redeclared here. Same values as the page.
  .fl-upgrade-modal {
    --fl-ink: #33413e;
    --fl-green: #5cc61e;
    --fl-green-ink: #4a7a2b;
    --fl-mint: #eaf6e6;
    --fl-pale: #f4f8f3;
    --fl-cream: #fcfcfa;
    --fl-sand: #f7f2e8;
    --fl-blue: #70c1ff;
    --fl-coral: #ef374e;
    --fl-line: #dfe8df;
    --fl-muted: #6f7d78;

    color: var(--fl-ink);
    font-family: 'Fira Sans', Arial, sans-serif;

    // This class lands on Modal's root, which is `.modal__mask`. Its 44px top padding clears a title
    // bar the wizard does not have, and it only centres vertically above 48em.
    padding-top: 0;
    align-items: center;
  }

  // The base modal is built for a 550px form: `.modal__wrapper` caps the width, caps the height at
  // 80% and scrolls the overflow, and `.modal__container` adds a white card with padding. The wizard
  // is its own 1050×780 panel that handles its own scrolling per step, so all of that has to be
  // undone — otherwise it renders small inside a second, outer scrollbar.
  :deep(.modal__wrapper) {
    max-width: none;
    max-height: none;
    overflow: visible;
    // flex-grow on the wrapper would stretch it; the panel decides its own width.
    flex-grow: 0;
    display: flex;
    justify-content: center;
  }

  :deep(.modal__container) {
    max-width: none;
    width: auto;
    margin: 0;
    padding: 0;
    background: none;
  }

  :deep(.modal__body) {
    margin: 0;
  }



  .fl-editor__note,
  .fl-editor__preview {
    margin: 24px 0 0;
    font-size: 11px;
    color: var(--fl-muted);
  }

  .fl-editor__preview {
    padding: 12px;
    background: #fdf3d7;
    color: var(--fl-ink);
  }

  /*
    The wizard's own message strip: its own grid row, under the header and spanning both columns.
    The row carries the colour, the strip inside it carries the layout.
  */
  .fl-notice-row {
    grid-column: 1 / -1;
  }

  .fl-notice {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 24px;
    font-size: 12px;
    line-height: 1.5;
  }

  .fl-notice--ok {
    background: var(--fl-mint);
    color: var(--fl-green-ink);
    border-bottom: 1px solid var(--fl-green);

    .fl-notice__action:hover {
      background: var(--fl-green-ink);
      color: #fff;
    }
  }

  .fl-notice--error {
    background: #fdecee;
    color: #a3202f;
    border-bottom: 1px solid var(--fl-coral);

    .fl-notice__action:hover {
      background: #a3202f;
      color: #fff;
    }
  }

  .fl-notice__text {
    flex: 1 1 auto;
    min-width: 0;
  }

  // The recovery, where there is one. Reads as the thing to press without becoming a second
  // primary button next to the footer's.
  .fl-notice__action {
    flex: 0 0 auto;
    border: 1px solid currentColor;
    background: #fff;
    padding: 6px 12px;
    border-radius: 4px;
    font-family: inherit;
    font-size: 11px;
    color: inherit;
    cursor: pointer;

    &:disabled {
      opacity: .5;
      cursor: default;
    }
  }

  .fl-notice__close {
    flex: 0 0 auto;
    border: 0;
    background: none;
    padding: 0;
    font-size: 20px;
    line-height: 1;
    color: inherit;
    opacity: .6;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }

  .fl-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    // Without this, a picker following the grid sat flush against the last input.
    margin-bottom: 24px;
  }

  .fl-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    > span {
      font-size: 10px;
      font-weight: 500;
    }

    // Dropdowns are FoodleapSelect, which carries its own matching styles.
    input,
    textarea {
      width: 100%;
      border: 1px solid var(--fl-line);
      padding: 12px;
      background: #fff;
      font-family: inherit;
      font-size: 13px;
      color: inherit;
    }

    input:focus,
    textarea:focus {
      outline: none;
      border-color: var(--fl-green);
    }

    textarea {
      resize: vertical;
    }
  }

  // A house number never needs a full column's worth of input.
  .fl-form-grid .fl-field--number input {
    max-width: 120px;
  }

  .fl-form-grid .fl-field:has(textarea),
  .fl-form-grid .fl-field--rich {
    grid-column: 1 / -1;
  }

  // RichTextArea carries the classic panels' own look — an unbordered editor with a bare button row
  // above it — which reads as broken inside this form, where every other field is a bordered box.
  // Wrapping it in the same border and pulling the toolbar inside as a ruled strip gives it the
  // shape of the textarea it replaced, focus ring included.
  //
  // :deep(), because the editor's markup is not this component's own — and every pseudo-class stays
  // *inside* the parens: nesting `&:hover` under a `:deep(.x)` block yields `:deep(.x):hover`,
  // which the scoping transform does not reliably rewrite.
  .fl-field--rich {
    :deep(.editor) {
      border: 1px solid var(--fl-line);
      background: #fff;
    }

    // The editor is a contenteditable, so :focus lands on a descendant rather than on the box.
    :deep(.editor:focus-within) {
      border-color: var(--fl-green);
    }

    :deep(.editor-buttons) {
      display: flex;
      flex-wrap: wrap;
      gap: 2px;
      align-items: center;
      padding: 6px 8px;
      border-bottom: 1px solid var(--fl-line);
      background: var(--fl-cream);
    }

    :deep(.rta__button) {
      border: 0;
      padding: 4px 7px;
      border-radius: 4px;
      font-family: inherit;
      font-size: 12px;
      line-height: 1;
      color: var(--fl-muted);
    }

    :deep(.rta__button:hover:not(:disabled)) {
      background: rgba(0, 0, 0, 0.05);
      color: var(--fl-ink);
    }

    :deep(.rta__button:disabled) {
      opacity: 0.35;
    }

    // The toolbar's own active colour is a hardcoded tomato; on this form it should be the accent.
    :deep(.is-active.rta__button) {
      background: var(--fl-mint);
      color: var(--fl-green-ink);
    }

    :deep(.editor__content) {
      padding: 0 12px 12px;
      font-family: inherit;
      font-size: 13px;
    }

    // Roughly the six rows the textarea reserved, without the classic panels' 200px floor.
    :deep(.ProseMirror) {
      min-height: 132px;
      padding-top: 12px;
    }
  }

  .fl-field--logo {
    grid-column: 1 / -1;
  }

  // Same badge as the profile-heading avatar, so the affordance reads identically in both places.
  .fl-logo-upload {
    position: relative;
    flex: 0 0 auto;
  }

  .fl-logo-upload__plus {
    position: absolute;
    right: -6px;
    bottom: -6px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--fl-green);
    color: #fff;
    display: grid;
    place-items: center;
    font-size: 17px;
    line-height: 1;
    // The file input sits underneath and covers the square; let clicks fall through to it.
    pointer-events: none;
  }

  .fl-logo-field {
    display: flex;
    align-items: center;
    gap: 20px;

    small {
      font-size: 9px;
      line-height: 1.6;
      color: var(--fl-muted);
    }

    // ImageInput ships unscoped global styles sized for the settings forms; these bring the
    // frame and its hover button down to the wizard's scale.
    :deep(.image-inp) {
      border-radius: 8px;
      overflow: hidden;
    }

    :deep(.image-inp--empty) {
      border: 1px dashed var(--fl-line);
      background: var(--fl-pale);
    }

    :deep(.image-input__action .button) {
      padding: 4px 8px;
      font-size: 9px;
      line-height: 1.2;
      white-space: nowrap;
    }
  }

  .fl-callout {
    padding: 16px;
    background: #eef3e8;
    border-left: 4px solid var(--fl-green);
    font-size: 11px;
    line-height: 1.5;
    margin-bottom: 24px;
  }

  // Groups a picker under its own heading, where a step covers several taxonomies.
  .fl-editor__sublabel {
    margin: 24px 0 12px;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--fl-muted);
  }

  /* Sits under the input it belongs to. Mint rather than green text so a field carrying a
     proposal is visible at a glance without shouting over the rest of the form. */
  .fl-suggestion {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
    margin-top: -2px;
    padding: 8px 10px;
    background: var(--fl-mint);
    font-size: 11px;
    line-height: 1.4;
    color: var(--fl-ink);

    button {
      border: 0;
      background: none;
      padding: 0;
      font-family: inherit;
      font-size: 11px;
      color: var(--fl-ink);
      text-decoration: underline;
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        color: var(--fl-green);
      }
    }
  }

  .fl-sdg {
    margin-bottom: 24px;
  }

  .fl-sdg__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;

    // The sublabel carries the block's top margin; its own bottom margin would double the gap
    // before the hint.
    .fl-editor__sublabel {
      margin-bottom: 0;
    }
  }

  .fl-sdg__count {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    font-size: 11px;
    color: var(--fl-muted);
    white-space: nowrap;
  }

  .fl-sdg__clear {
    border: 0;
    background: none;
    padding: 0;
    font-size: 11px;
    color: var(--fl-coral);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .fl-sdg__hint {
    margin: 8px 0 12px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--fl-muted);
  }

  .fl-sdg__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    button {
      display: block;
      width: 62px;
      height: 62px;
      padding: 0;
      border: 0;
      background: none;
      cursor: pointer;
      // Unselected goals are dimmed rather than hidden, so the whole set stays browsable and the
      // chosen ones read as picked at a glance.
      opacity: .35;
      filter: grayscale(1);
      transition: opacity .2s, filter .2s;

      img {
        display: block;
        width: 100%;
        height: 100%;
      }

      &:hover {
        opacity: .7;
        filter: grayscale(.4);
      }

      &.is-on {
        opacity: 1;
        filter: none;
      }

      &:focus-visible {
        outline: 2px solid var(--fl-green);
        outline-offset: 2px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      button {
        transition: none;
      }
    }
  }

  .fl-offer-remarks {
    display: flex;
    flex-direction: column;
    gap: 16px;

    // The sublabel's own top margin already spaces the group from the picker above it.
    .fl-editor__sublabel {
      margin-bottom: 0;
    }

    &__parent {
      font-style: normal;
      font-weight: 400;
      color: var(--fl-muted);
    }
  }

  // The whole transitions step in one glance: name, our suggested score, three choices.
  .fl-triage {
    border: 1px solid var(--fl-line);
    margin-bottom: 24px;
  }

  .fl-triage__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;

    & + & {
      border-top: 1px solid var(--fl-line);
    }

    > i {
      flex: 0 0 10px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }
  }

  .fl-triage__name {
    flex: 1 1 auto;
    min-width: 0;

    b {
      display: block;
      font-size: 13px;
      font-weight: 500;
    }

    small {
      display: block;
      margin-top: 4px;
      font-size: 10px;
      color: var(--fl-muted);
    }
  }

  .fl-triage__choice {
    display: inline-flex;
    flex: 0 0 auto;

    button {
      border: 1px solid var(--fl-line);
      border-left-width: 0;
      background: #fff;
      padding: 8px 12px;
      font-family: inherit;
      font-size: 11px;
      color: var(--fl-muted);
      cursor: pointer;
      white-space: nowrap;

      &:first-child {
        border-left-width: 1px;
      }

      &:hover {
        color: var(--fl-ink);
        border-color: var(--fl-green);
      }

      &.is-on {
        border-color: var(--fl-green);
        background: var(--fl-mint);
        color: var(--fl-ink);
        font-weight: 500;
      }
    }
  }

  .fl-story-rule {
    grid-column: 1 / -1;
    margin: 0;
    font-size: 12px;
    line-height: 1.55;
    color: var(--fl-muted);
  }

  // Our text, in the actor's form. Visually distinct so it never reads as their own words.
  .fl-suggestion {
    grid-column: 1 / -1;
    margin: 0;
    padding: 12px 16px;
    border-left: 2px solid var(--fl-blue);
    background: var(--fl-pale);
    font-size: 12px;
    line-height: 1.55;
    color: var(--fl-muted);

    b {
      color: var(--fl-ink);
      font-weight: 500;
    }
  }

  // One story field per row. Side by side, ambition and activities each got half the width of a
  // panel that is already indented, which is too narrow for the couple of sentences they ask for.
  .fl-transition-form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 4px 4px 24px;

    label {
      display: flex;
      flex-direction: column;
      gap: 8px;

      > span {
        font-size: 10px;
        font-weight: 500;
      }
    }

    input,
    textarea {
      width: 100%;
      border: 1px solid var(--fl-line);
      padding: 12px;
      background: #fff;
      font-family: inherit;
      font-size: 13px;
      color: inherit;
    }

    input:focus,
    textarea:focus {
      outline: none;
      border-color: var(--fl-green);
    }

    textarea {
      min-height: 80px;
      resize: vertical;
    }
  }

  // One roadmap entry per row, each cell labelled. The previous version relied on placeholders
  // alone and declared five grid columns for six children, so the last input wrapped.
  .fl-roadmap {
    margin-bottom: 12px;
  }

  /* Evidence and its link wrap onto a second line: seven columns in one row left every field
     about 100px wide, which is unusable for a URL. */
  .fl-roadmap__row {
    display: grid;
    grid-template-columns: 1.4fr .8fr 1fr .5fr 28px;
    gap: 8px;
    align-items: end;
    padding: 12px;
    margin-bottom: 8px;
    border: 1px solid var(--fl-line);
    background: var(--fl-pale);
  }
  .fl-roadmap__cell--evidence {
    grid-column: 1 / 3;
  }
  .fl-roadmap__cell--link {
    grid-column: 3 / 6;
  }

  .fl-roadmap__cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    > span {
      font-size: 9px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: .08em;
      color: var(--fl-muted);
    }

    input {
      width: 100%;
      min-width: 0;
      border: 1px solid var(--fl-line);
      padding: 12px;
      background: #fff;
      font-family: inherit;
      font-size: 13px;
      color: inherit;

      &:focus {
        outline: none;
        border-color: var(--fl-green);
      }
    }
  }

  .fl-roadmap__remove {
    align-self: end;
    height: 38px;
    border: 0;
    background: none;
    font-size: 18px;
    line-height: 1;
    color: var(--fl-muted);
    cursor: pointer;

    &:hover {
      color: var(--fl-coral);
    }
  }

  // Evidence rows inside a transition, spanning the transition form's two columns.
  .fl-evidence {
    grid-column: 1 / -1;

    > span {
      display: block;
      font-size: 10px;
      font-weight: 500;
      margin-bottom: 4px;
    }
  }

  .fl-evidence__hint {
    margin: 0 0 12px;
    font-size: 11px;
    line-height: 1.5;
    color: var(--fl-muted);
  }

  .fl-evidence__row {
    display: grid;
    grid-template-columns: 1.2fr 1.2fr .6fr 28px;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
  }

  .fl-evidence__remove {
    border: 0;
    background: none;
    font-size: 18px;
    line-height: 1;
    color: var(--fl-muted);
    cursor: pointer;

    &:hover {
      color: var(--fl-coral);
    }
  }

  // A read-only list inside the wizard, for things edited elsewhere.
  .fl-editor__list {
    margin: 0;
    padding-left: 20px;
    font-size: 12px;

    li {
      margin-bottom: 4px;
    }
  }

  .fl-add-row {
    border: 1px dashed var(--fl-green);
    color: var(--fl-green);
    background: none;
    padding: 12px;
    width: 100%;
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;

    &:disabled {
      opacity: .5;
      cursor: default;
    }
  }

  /* The rows for the existing links, and the field that adds another. */
  .fl-products__row {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid var(--fl-line);
    background: #fff;
    padding: 10px 12px;
    font-size: 13px;

    // The name takes the row; the link and the remove control sit at the end of it.
    > span {
      flex: 1 1 auto;
      min-width: 0;
    }

    button {
      border: 0;
      background: none;
      font-size: 18px;
      line-height: 1;
      color: var(--fl-muted);
      cursor: pointer;

      &:hover {
        color: var(--fl-coral);
      }

      &:disabled {
        opacity: .5;
        cursor: default;
      }
    }

    // Declared after the shared `button` rule above, which sizes the 18px × control: this one is a
    // text button and would otherwise inherit that size.
    .fl-products__open {
      flex: 0 0 auto;
      font-size: 10px;
      color: var(--fl-green-ink);
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
      white-space: nowrap;

      &:hover {
        color: var(--fl-ink);
      }
    }

    // The row is the header of the form underneath it, so it gives up its bottom border to it.
    &.is-open {
      border-bottom-color: transparent;
      background: var(--fl-pale);
    }
  }

  // One product's own profile, edited inside the step that lists it. Boxed with the row above it so
  // it reads as that row's contents rather than as a second block on the step.
  //
  // The column gap is the single source of vertical rhythm here: `.fl-form-grid` and `.fl-picker`
  // each carry a 24px bottom margin of their own for the steps that stack them directly, and
  // leaving those in place on top of the gap is what left the groups drifting apart unevenly.
  .fl-product-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
    border: 1px solid var(--fl-line);
    border-top: 0;
    background: var(--fl-pale);
    padding: 20px 16px 22px;

    .fl-form-grid,
    :deep(.fl-picker) {
      margin-bottom: 0;
    }
  }

  .fl-product-form__note {
    margin: 0;
    font-size: 11px;
    line-height: 1.6;
    color: var(--fl-muted);
  }

  // A named group inside the form, ruled off like step 2 separates its own groups.
  .fl-product-form__section {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-top: 20px;
    border-top: 1px solid var(--fl-line);
  }

  .fl-product-form__heading {
    margin: 0;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--fl-muted);
  }

  // The native input is hidden rather than styled: the app's global `input[type=checkbox]` rules
  // draw a tick through an oversized ::after, which cannot be undone from a scoped block without
  // fighting it. `<i>` is the visible box — the same one the taxonomy picker's options use.
  .fl-product-form__check {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    cursor: pointer;

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: 0;
      opacity: 0;
      pointer-events: none;
    }

    i {
      flex: 0 0 14px;
      width: 14px;
      height: 14px;
      border: 1px solid var(--fl-line);
      background: #fff;
    }

    // Tick drawn rather than an entity, so it scales with the box and needs no font support.
    input:checked + i {
      border-color: var(--fl-green);
      background: var(--fl-green);
      background-image: linear-gradient(45deg, transparent 42%, #fff 42%, #fff 55%, transparent 55%),
        linear-gradient(-45deg, transparent 62%, #fff 62%, #fff 74%, transparent 74%);
    }

    input:focus-visible + i {
      outline: 2px solid var(--fl-green);
      outline-offset: 2px;
    }
  }

  .fl-product-form__actions {
    display: flex;
    align-items: center;
    gap: 16px;
    padding-top: 20px;
    border-top: 1px solid var(--fl-line);

    .fl-add-row {
      width: auto;
      flex: 0 0 auto;
      white-space: nowrap;
    }

    a {
      font-size: 10px;
      color: var(--fl-green-ink);
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
    }
  }

  // The button sizes itself to its label; the field takes the rest.
  .fl-products__add {
    display: flex;
    align-items: stretch;
    gap: 8px;

    .fl-add-row {
      width: auto;
      flex: 0 0 auto;
      white-space: nowrap;
    }
  }

  /* --- responsive ------------------------------------------------------ */

  @media (max-width: 560px) {
    .fl-form-grid,
    .fl-roadmap__row,
    .fl-evidence__row {
      grid-template-columns: 1fr;
    }

    .fl-roadmap__remove,
    .fl-evidence__remove {
      justify-self: start;
      height: auto;
    }
  }
</style>
